/**
 * Cushionfit configurator — WEB COMPONENT (orchestration)
 * ------------------------------------------------------------
 * Mounts inside the `cushion-configurator` section. Renders config- and
 * shape-driven controls for a SET of pieces, holds the capture model (Order) as
 * state, and on every change recomputes per-piece + set pricing and re-renders
 * the live previews.
 *
 * Deliberately thin: it owns DOM + state only. Pricing, preview geometry, shape
 * definitions, the data model and the supplier handoff each live in their own
 * module. Option lists/coefficients come from configurator-config.js; shapes
 * (and their dimensions) from configurator-shapes.js — both stubbed for partner
 * data.
 *
 * Two kinds of state, deliberately separated:
 *   - capture model (#order): what feeds pricing + the supplier handoff. Pure,
 *     serializable. Holds only a template *filename* (templateRef), never a File.
 *   - view state (#collapsed, #templates): UI-only. The uploaded File objects
 *     live here keyed by piece id; they get sent to Shopify at cart-add (still
 *     deferred per brief), keeping the model clean.
 *
 * Events are delegated from the root, so structural re-renders don't drop
 * listeners and per-keystroke updates touch only the affected card.
 *
 * @typedef {import('./configurator-model.js').Order} Order
 * @typedef {import('./configurator-model.js').Piece} Piece
 */

import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { CONFIG, optionById, optionListFor, findOption, starterById, exposureById, visibleFabrics, visibleFoams } from '#config';
import { UNITS, asUnit, toMm, displayValue, formatDim, fieldBounds, DEFAULT_UNIT } from '#units';
import { createOrder, createPiece, clonePiece, setPieceShape, applyExposureFoam } from '#model';
import { SHAPES, SELECTABLE_SHAPES, DEPTH_FIELD, shapeById, shapeDefaults, defaultZipEdge, sideProfileSvg } from '#shapes';
import { pricePiece, priceOrder, fitGuaranteeAmount, pieceQuoteReason, multiBuyState, formatMoney } from '#pricing';
import { validatePiece, validateOrder } from '#validation';
import { nudgesForPiece, foamExposureMessage, fabricExposureMessage } from '#nudges';
import { measureDiagram } from '#diagrams';
import { createCart } from '#cart';
import { orderToToken, tokenToOrder, encodeOrder, decodeOrder } from '#serialize';
import { estimateDelivery } from '#leadtime';
import { track } from '#analytics';

/** Chevron used by the collapse toggle (rotates via CSS). */
const CARET = `<svg class="cf__caret" viewBox="0 0 16 16" aria-hidden="true" width="14" height="14">
  <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5"
        stroke-linecap="round" stroke-linejoin="round"/></svg>`;

/** Tick used by the fit-guarantee toggle (shown when opted in). */
const CHECK = `<svg viewBox="0 0 16 16" aria-hidden="true" width="13" height="13">
  <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2"
        stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** "Recommended" badge markup when `id` is the exposure-recommended option. */
const recBadge = (id, recommendedId) =>
  id != null && id === recommendedId ? ' <span class="cf__chip-rec">Recommended</span>' : '';

/** Retail prices are whole-£5 multiples, so guide/summary lines drop the ".00". */
const gbpShort = (n) => formatMoney(n).replace(/\.00$/, '');

/** "£115" or "£115–£135" across a set of unit prices — a stated range, never a
 *  "from" (the seed sizes are a starting point, not a floor). */
const priceSpan = (units) => {
  const lo = Math.min(...units);
  const hi = Math.max(...units);
  return lo === hi ? gbpShort(lo) : `${gbpShort(lo)}–${gbpShort(hi)}`;
};

/** Max template/reference photos per bespoke build. MUST match the Worker's
 *  `QUOTE_LIMITS.photos` (webhook/src/index.mjs) — they ride as base64 email
 *  attachments, so the count is bounded to keep the message small. */
const MAX_QUOTE_PHOTOS = 4;

class CushionConfigurator extends Component {
  /** @type {Order} */
  #order;

  /** View state — ids of collapsed pieces. Kept off the capture model. */
  #collapsed = new Set();

  /** View state — uploaded templates keyed by piece id: an ARRAY of
   *  { file, url, name } (a bespoke piece can carry several reference photos;
   *  the first is the primary that satisfies validation + the spec's templateRef). */
  #templates = new Map();

  /** Cart strategy (variant-ladder; mock until the ladder product exists). */
  #cart;

  /** 'wizard' | 'single' — set from the section. Wizard can fall back to single. */
  #flow = 'wizard';

  /** Wizard step index: 0..pieces.length-1 are pieces, pieces.length is Review. */
  #step = 0;

  /** View state — dimension display unit: 'cm' (default) | 'in'. Off the model. */
  #unit = DEFAULT_UNIT;

  /** View state — notice after rehydrating from a link: null = none, [] = clean,
   *  [..] = warnings (e.g. an option in the link is no longer offered). */
  #shareWarnings = null;

  /** View state — a recoverable draft offered on the chooser: { order, at } | null. */
  #pendingDraft = null;

  /** Funnel stages already reported this build (so GA4 gets clean, deduped steps). */
  #trackedStages = new Set();

  /** The objection the customer tapped on the review, if any (see config.objections).
   *  Per-build session state — never on the capture model, resets each build. Rides
   *  the abandonment beacon + a `cf_objection` event; captures WHY, not just where. */
  #objection = null;

  /**
   * Made-to-measure cancellation acknowledgement (CCR 2013 reg 28(1)(b)) — blocks
   * add-to-cart until ticked. Session state ONLY: deliberately never on the capture
   * model, so it can't ride a share link or a saved draft and hand someone else's
   * tick to a new customer. Every customer must acknowledge it themselves.
   */
  #consentMadeToMeasure = false;

  /** Gate so the initial seed render doesn't write a draft before any user edit. */
  #ready = false;

  /** Saved-designs library: logged-in customer id + app-proxy endpoint (or null). */
  #customerId = null;
  #libraryEndpoint = null;

  /** Bespoke quote endpoint (app-proxy /quote path), or null when not wired. */
  #quoteEndpoint = null;

  /** Order sense-check photos: app-proxy /sensecheck endpoint + the logged-in
   *  customer's identity (any may be absent), plus the live File objects the
   *  customer attaches at the review. The Files are VIEW STATE — never on the
   *  model — and ride to the team on add-to-cart so a human can check the build. */
  #sensecheckEndpoint = null;
  #customerEmail = null;
  #customerName = null;
  #sensecheckPhotos = []; // [{ id, file, url }]

  /** "Email me my design" endpoint (app-proxy /design-email), or null when not
   *  wired. The open/closed state of its panel is view state, off the model. */
  #designEmailEndpoint = null;
  #emailDesignOpen = false;

  /* --- anonymous abandoned-build capture ---------------------------------
   * Records what people configure but don't buy, so drop-off can be read
   * against PRICE as well as step. Deliberately carries no personal data —
   * see the PRIVACY note on the Worker's /abandoned route.
   *
   * The session id is generated per page load and held ONLY in memory: writing
   * it to localStorage would make it a device identifier and pull this into
   * PECR consent territory, for no analytical gain (we group by session, never
   * by person).
   */
  #abandonedEndpoint = null;
  #sessionId = null;
  #abandonTimer = null;
  #abandonStage = 'start';
  #abandonFlags = { converted: 0, overLadder: 0, quoted: 0 };
  #lastAbandonPayload = null;

  connectedCallback() {
    super.connectedCallback();
    // Order of precedence for the initial order:
    //   1. a shared/saved-design link (?build=<token>) — explicit intent, wins;
    //   2. a forced starter from the section (landing page / product) — seeds + skips the chooser;
    //   3. otherwise null → the starter chooser is shown first.
    // (Read here, not the constructor, where attributes aren't reliable.)
    const shared = this.#orderFromUrl();
    if (shared) {
      this.#order = shared.order;
      this.#shareWarnings = shared.warnings;
    } else {
      const forced = this.dataset.furnitureType;
      this.#order = forced && starterById(forced) ? createOrder(forced) : null;
      // No token, no forced starter → we'd show the chooser. Offer to resume an
      // unfinished design from a previous visit (same device) instead of blank.
      if (!this.#order) this.#pendingDraft = this.#loadDraft();
    }
    this.#flow = this.dataset.flow === 'single' ? 'single' : 'wizard';
    this.#customerId = this.dataset.customer || null; // empty unless logged in
    this.#libraryEndpoint = this.dataset.library || null; // app-proxy path, or absent
    this.#quoteEndpoint = this.dataset.quote || null; // app-proxy /quote path, or absent
    this.#sensecheckEndpoint = this.dataset.sensecheck || null; // app-proxy /sensecheck, or absent (mock)
    this.#customerEmail = this.dataset.customerEmail || null; // empty unless logged in
    this.#customerName = this.dataset.customerName || null; // empty unless logged in
    this.#designEmailEndpoint = this.dataset.designEmail || null; // absent = mock mode
    this.#abandonedEndpoint = this.dataset.abandoned || null; // absent = capture off
    this.#unit = this.#initialUnit();
    this.#cart = createCart();
    this.#render();
    this.#bindEvents();
    if (this.#order) this.#refreshAll();
    this.#ready = true; // from here, every change auto-saves a draft
  }

  /** Remembered display unit (persisted so a returning customer keeps inches). */
  #initialUnit() {
    try {
      const saved = localStorage.getItem('cf-unit');
      if (saved) return asUnit(saved);
    } catch {
      /* storage blocked (private mode) — fall back to the default */
    }
    return DEFAULT_UNIT;
  }

  /* ---- rendering ------------------------------------------------------- */

  #render() {
    if (!this.#order) {
      this.innerHTML = this.#renderChooser();
      return;
    }
    this.innerHTML = this.#flow === 'wizard' ? this.#renderWizard() : this.#renderSingle();
  }

  /** "Resume your last design?" banner — only on the chooser, only if a draft exists. */
  #resumeBannerMarkup() {
    if (!this.#pendingDraft) return '';
    const ago = this.#savedAgo(this.#pendingDraft.at);
    const n = this.#pendingDraft.order.pieces.length;
    return `
      <div class="cf__resume" role="status">
        <div class="cf__resume-body">
          <strong>Pick up where you left off</strong>
          <span class="cf__resume-sub">An unfinished design (${n} cushion${n === 1 ? '' : 's'}) saved ${ago}.</span>
        </div>
        <div class="cf__resume-actions">
          <button type="button" class="cf__btn" data-action="discard-draft">Discard</button>
          <button type="button" class="cf__resume-btn" data-action="resume-draft">Resume design</button>
        </div>
      </div>`;
  }

  /** Guide-price line for a starter tile, so the expectation is set BEFORE the
   *  full set total renders (a 6-cushion preset opening on an unframed £750
   *  reads as sticker shock, not value). Deliberately a GUIDE, never "from":
   *  it prices the preset's seed sizes, which are a starting point the customer
   *  will edit — not a floor, and not a universal cost for the furniture type.
   *  Multi-piece presets show the per-cushion range across their pieces. Null
   *  when the preset can't be instant-priced (bespoke piece) — no price shown. */
  #starterGuide(starterId) {
    try {
      const order = createOrder(starterId);
      if (order.pieces.some((p) => shapeById(p.shape).requiresTemplate)) return null;
      const units = order.pieces.map((p) => pricePiece(p).unitTotal).filter((u) => Number.isFinite(u) && u > 0);
      if (!units.length) return null;
      const span = priceSpan(units);
      const count = order.pieces.reduce((n, p) => n + p.quantity, 0);
      // Count is shown so two sets with same-size cushions (2-seat vs 3-seat)
      // don't read as an identical copy-paste price. Per-cushion is kept
      // deliberately (see hero-build.js) — it preserves the low entry anchors
      // and defers the set total to the review, where it's contextualised.
      return count > 1 ? `Guide price ${span} per cushion · ${count} in the set` : `Guide price ${span}`;
    } catch {
      return null;
    }
  }

  /** Step 0: "what are you making cushions for?" — pick a starter or start blank. */
  #renderChooser() {
    const cards = (CONFIG.starters ?? [])
      .map((s) => {
        const guide = this.#starterGuide(s.id);
        return `
        <li>
          <button type="button" class="cf__starter" data-action="choose-starter" data-starter="${s.id}">
            <span class="cf__starter-label">${escapeHtml(s.label)}</span>
            ${s.blurb ? `<span class="cf__starter-blurb">${escapeHtml(s.blurb)}</span>` : ''}
            ${guide ? `<span class="cf__starter-price">${escapeHtml(guide)}</span>` : ''}
          </button>
        </li>`;
      })
      .join('');
    return `
      <div class="cf cf--chooser">
        ${this.#resumeBannerMarkup()}
        <div class="cf__chooser-head">
          <h3 class="cf__chooser-title">What are you making cushions for?</h3>
          <p class="cf__chooser-sub">Pick a starting point — you can change everything after.</p>
        </div>
        <ul class="cf__starters" role="list">
          ${cards}
          <li>
            <button type="button" class="cf__starter cf__starter--scratch" data-action="choose-starter" data-starter="">
              <span class="cf__starter-label">Start from scratch</span>
              <span class="cf__starter-blurb">Build a single cushion from blank</span>
            </button>
          </li>
        </ul>
      </div>`;
  }

  /** Subtle "start over" affordance that returns to the chooser. */
  /** Decode an order from a ?build=<token> share/saved-design link, if present. */
  #orderFromUrl() {
    if (typeof location === 'undefined') return null;
    try {
      const token = new URLSearchParams(location.search).get('build');
      return token ? tokenToOrder(token) : null;
    } catch {
      return null;
    }
  }

  /* ---- draft auto-save (same-device recovery) -------------------------- */

  /** Persist the live design so a returning customer can pick it back up. Stores
   *  the same versioned envelope as a share link (one serializer, three uses). */
  #saveDraft() {
    if (!this.#order) return;
    try {
      localStorage.setItem('cf-draft', JSON.stringify({ at: Date.now(), order: encodeOrder(this.#order) }));
    } catch {
      /* storage blocked (private mode / quota) — drafts just won't persist */
    }
  }

  /** Pending debounced draft write (slider drags fire dozens of changes/sec —
   *  encode + a synchronous localStorage write per tick janks low-end phones). */
  #draftTimer = null;

  #scheduleDraftSave() {
    if (!this.#ready) return;
    clearTimeout(this.#draftTimer);
    this.#draftTimer = setTimeout(() => {
      this.#draftTimer = null;
      this.#saveDraft();
    }, 300);
  }

  /** Flush a PENDING save (pagehide / teardown). Only when one is pending — an
   *  unconditional save here would resurrect the draft cleared at add-to-cart. */
  #flushDraft = () => {
    if (this.#draftTimer == null) return;
    clearTimeout(this.#draftTimer);
    this.#draftTimer = null;
    this.#saveDraft();
  };

  /* --- abandoned-build beacon --------------------------------------------
   * Fires on a long debounce off the same chokepoint as the draft save, and
   * upserts on the session id — so the LAST beacon of a session is the state
   * the customer walked away from.
   *
   * Deliberately NOT driven off `pagehide`/`sendBeacon` alone. That looks like
   * the natural fit, but a beacon through a Shopify app proxy during unload is
   * unreliable, and we'd silently lose sessions with no way to tell. Sending
   * during the session and letting the last write win degrades gracefully
   * instead: a dropped request costs precision, never the whole session.
   */
  #markStage(stage) {
    const order = ['start', 'shape', 'dimensions', 'options', 'review', 'cart'];
    if (order.indexOf(stage) > order.indexOf(this.#abandonStage)) this.#abandonStage = stage;
  }

  #scheduleAbandonBeacon({ immediate = false } = {}) {
    if (!this.#abandonedEndpoint || !this.#ready || !this.#order) return;
    clearTimeout(this.#abandonTimer);
    if (immediate) return void this.#sendAbandonBeacon();
    // Long, because we want the SETTLED state, not every keystroke.
    this.#abandonTimer = setTimeout(() => {
      this.#abandonTimer = null;
      this.#sendAbandonBeacon();
    }, 8000);
  }

  #sendAbandonBeacon() {
    if (!this.#abandonedEndpoint || !this.#order) return;
    let payload;
    try {
      this.#sessionId ??= this.#newSessionId();
      payload = {
        sessionId: this.#sessionId,
        token: orderToToken(this.#order),
        stage: this.#abandonStage,
        total: Number(priceOrder(this.#order).total.toFixed(2)),
        pieceCount: this.#order.pieces.length,
        ...this.#abandonFlags,
        ...(this.#objection ? { objection: this.#objection } : {}),
      };
    } catch {
      return; // an unpriceable/unencodable build is not worth reporting
    }
    // Skip an identical repeat (idle re-fires, re-renders that change nothing).
    const fingerprint = JSON.stringify(payload);
    if (fingerprint === this.#lastAbandonPayload) return;
    this.#lastAbandonPayload = fingerprint;

    try {
      fetch(this.#abandonedEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: fingerprint,
        keepalive: true, // survives a navigation started right after
      }).catch(() => {});
    } catch {
      /* telemetry must never break the configurator */
    }
  }

  /** Random, per page load, memory only — never persisted (see the field note). */
  #newSessionId() {
    try {
      return [...crypto.getRandomValues(new Uint8Array(12))]
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    } catch {
      return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
    }
  }

  #clearDraft() {
    clearTimeout(this.#draftTimer); // a pending save must not outlive the clear
    this.#draftTimer = null;
    try {
      localStorage.removeItem('cf-draft');
    } catch {
      /* ignore */
    }
  }

  /** Load + validate a saved draft, ignoring (and clearing) stale or junk ones. */
  #loadDraft() {
    const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
    try {
      const raw = localStorage.getItem('cf-draft');
      if (!raw) return null;
      const { at, order } = JSON.parse(raw);
      if (!at || Date.now() - at > THIRTY_DAYS) {
        this.#clearDraft();
        return null;
      }
      const decoded = decodeOrder(order); // sanitises against current config
      if (!decoded?.order.pieces.length) {
        this.#clearDraft();
        return null;
      }
      return { order: decoded.order, at };
    } catch {
      return null;
    }
  }

  /** Friendly "saved N ago" for the resume banner. */
  #savedAgo(at) {
    const mins = Math.max(0, Math.round((Date.now() - at) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  /** Drop a stale ?build= token from the address bar (e.g. after Start over). */
  #clearBuildParam() {
    if (typeof location === 'undefined') return;
    try {
      const url = new URL(location.href);
      if (!url.searchParams.has('build')) return;
      url.searchParams.delete('build');
      history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch {
      /* history blocked — harmless, the param just lingers */
    }
  }

  /** Top bar above the build: Start over (left) + Save / Share (right). */
  #topBar() {
    const restart = CONFIG.starters?.length
      ? `<button type="button" class="cf__restart" data-action="restart">&larr; Start over</button>`
      : '<span></span>';
    return `
      <div class="cf__topbar">
        ${restart}
        <div class="cf__topbar-actions">
          ${this.#libraryButton()}
          <button type="button" class="cf__share" data-action="share" title="Copy a link that rebuilds this exact design">
            <span data-share-label>Copy share link</span>
          </button>
          <button type="button" class="cf__share cf__share--email" data-action="toggle-email-design"
            aria-expanded="${this.#emailDesignOpen}"
            title="We’ll email you a link that reopens this exact design">
            Email me this design
          </button>
        </div>
      </div>
      ${this.#emailDesignPanel()}
      ${this.#shareNoticeMarkup()}`;
  }

  /** Inline "email me my design" capture — the exit ramp for a buyer who isn't
   *  ready today: their rebuild link lands in their inbox instead of being lost
   *  with the tab. The email address is read from the DOM on submit and sent
   *  straight to the Worker — it never touches the (pure) capture model or the
   *  anonymous analytics beacon. */
  #emailDesignPanel() {
    if (!this.#emailDesignOpen) return '';
    return `
      <div class="cf__email-design" data-email-design>
        <p class="cf__email-design-intro">Not ready to order? We’ll email you a link that reopens this exact design — sizes, fabric and all.</p>
        <div class="cf__email-design-row">
          <label class="cf__quote-label cf__email-design-label">Your email
            <input class="cf__quote-input" type="email" data-email-design-input autocomplete="email" required />
          </label>
          <button type="button" class="cf__btn cf__email-design-send" data-action="send-design-email">Email it to me</button>
        </div>
        <input type="text" class="cf__quote-hp" data-email-design-hp tabindex="-1" autocomplete="off" aria-hidden="true" />
        <p class="cf__email-design-status" data-email-design-status role="status" hidden></p>
        <p class="cf__email-design-fine">One email with your design link — no mailing list, nothing else.</p>
      </div>`;
  }

  /** "Save to my designs" — only for a logged-in customer with the library wired. */
  #libraryButton() {
    if (!this.#customerId || !this.#libraryEndpoint) return '';
    return `<button type="button" class="cf__share cf__share--save" data-action="save-design"
              title="Save this design to your account">
              <span data-save-label>Save to my designs</span>
            </button>`;
  }

  /** One-time banner shown when the build came from a shared/saved link. */
  #shareNoticeMarkup() {
    if (this.#shareWarnings == null) return '';
    const list = this.#shareWarnings.length
      ? `<ul class="cf__notice-list">${this.#shareWarnings.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>`
      : '';
    return `
      <div class="cf__notice" role="status">
        <div class="cf__notice-body"><strong>Your saved design is loaded — re-priced at today’s rates.</strong>${list}</div>
        <button type="button" class="cf__notice-close" data-action="dismiss-notice" aria-label="Dismiss">&times;</button>
      </div>`;
  }

  /** Build a shareable link for the current design, drop it in the URL + clipboard. */
  async #shareDesign(btn) {
    if (!this.#order) return;
    const url = `${location.origin}${location.pathname}?build=${orderToToken(this.#order)}`;
    try {
      history.replaceState(null, '', url);
    } catch {
      /* some embedded contexts block history — the copy below still works */
    }
    let copied = false;
    try {
      await navigator.clipboard.writeText(url);
      copied = true;
    } catch {
      /* clipboard blocked (insecure context / permissions) — URL is in the address bar */
    }
    const label = btn.querySelector('[data-share-label]') ?? btn;
    const prev = label.textContent;
    label.textContent = copied ? 'Link copied!' : 'Link is in the address bar';
    setTimeout(() => {
      label.textContent = prev;
    }, 2200);
  }

  /** A sensible default name for the saved design (starter + piece count). */
  #defaultDesignName() {
    const starter = this.#order?.furnitureType ? starterById(this.#order.furnitureType) : null;
    const n = this.#order?.pieces.length ?? 0;
    return `${starter?.label ?? 'My cushions'} (${n} piece${n === 1 ? '' : 's'})`;
  }

  /** Save the current design to the customer's account (via the signed app proxy). */
  async #saveToLibrary(btn) {
    if (!this.#order || !this.#libraryEndpoint) return;
    const name = window.prompt('Name this design', this.#defaultDesignName());
    if (name === null) return; // cancelled
    const label = btn.querySelector('[data-save-label]') ?? btn;
    const prev = label.textContent;
    btn.disabled = true;
    label.textContent = 'Saving…';
    try {
      const res = await fetch(this.#libraryEndpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'save', name, token: orderToToken(this.#order) }),
      });
      if (res.status === 401) {
        label.textContent = 'Please log in';
      } else if (!res.ok) {
        throw new Error(String(res.status));
      } else {
        label.textContent = 'Saved ✓';
      }
    } catch {
      label.textContent = 'Save failed — try again';
    } finally {
      btn.disabled = false;
      setTimeout(() => {
        label.textContent = prev;
      }, 2400);
    }
  }

  /** Single-page layout: every piece stacked, sticky summary. */
  #renderSingle() {
    return `
      <div class="cf">
        ${this.#topBar()}
        <div class="cf__pieces">
          ${this.#order.pieces.map((p, i) => this.#pieceCard(p, i)).join('')}
        </div>
        ${this.#needsQuote() ? '' : this.#guaranteeToggle()}
        ${this.#needsQuote() ? '' : this.#consentToggle()}
        ${this.#needsQuote() ? this.#quoteForm() : ''}
        <div class="cf__summary">
          <button type="button" class="cf__add" data-action="add">+ Add another piece</button>
          ${this.#needsQuote() ? '' : this.#multiBuyNudge()}
          <div class="cf__summary-end">
            ${this.#needsQuote() ? this.#quotePriceNote() : this.#totalMarkup()}
            ${this.#checkoutControl()}
          </div>
          ${this.#reassuranceMarkup()}
        </div>
        ${this.#feedbackMarkup()}
      </div>`;
  }

  #totalMarkup() {
    return `
      <div class="cf__totals">
        <div class="cf__total-line cf__total-line--save" data-multibuy-line hidden>
          <span class="cf__total-line-label" data-multibuy-label>Set discount</span>
          <span class="cf__total-line-value" data-multibuy-amount>—</span>
        </div>
        <div class="cf__total-line" data-guarantee-line hidden>
          <span class="cf__total-line-label">${escapeHtml(CONFIG.fitGuarantee?.label ?? 'Fit guarantee')}</span>
          <span class="cf__total-line-value" data-guarantee-amount>—</span>
        </div>
        <div class="cf__total">
          <span class="cf__total-label">Set total</span>
          <span class="cf__total-value" data-order-total aria-live="polite">—</span>
        </div>
        <div class="cf__total-sub" data-total-breakdown hidden></div>
      </div>`;
  }

  /** One-line reframe under the set total — "6 cushions · £115–£135 each" — so
   *  the headline number carries its own explanation. A range, not "from": it
   *  states what the pieces in THIS build cost, with no floor implied. Empty
   *  (line hidden) for a single cushion, where the total already IS the
   *  per-cushion price. */
  #totalBreakdown() {
    const pieces = this.#order.pieces;
    const count = pieces.reduce((n, p) => n + p.quantity, 0);
    if (count <= 1) return '';
    const units = pieces.map((p) => pricePiece(p).unitTotal).filter((u) => Number.isFinite(u) && u > 0);
    if (!units.length) return '';
    return `${count} cushions · ${priceSpan(units)} each`;
  }

  /** Multi-buy nudge content for the current order (single source: multiBuyState).
   *  Returns {html, hidden, applied} so markup + live refresh share one code path. */
  #multiBuyNudgeParts() {
    const s = multiBuyState(this.#order);
    if (!s.enabled) return { html: '', hidden: true, applied: false };
    const pct = Math.round(s.rate * 100);
    if (s.applies) {
      return {
        html: `<span class="cf__multibuy-tick" aria-hidden="true">✓</span> <strong>${pct}% off</strong> applied to your set`,
        hidden: false,
        applied: true,
      };
    }
    const n = s.toNext;
    return {
      html: `Add ${n} more cushion${n === 1 ? '' : 's'} and <strong>save ${pct}%</strong> — sets of ${s.threshold}+`,
      hidden: false,
      applied: false,
    };
  }

  /** The "build the set" nudge, rendered next to "Add another piece". A no-op
   *  (hidden, empty) until multiBuyDiscount.enabled — see config. */
  #multiBuyNudge() {
    const { html, hidden, applied } = this.#multiBuyNudgeParts();
    return `<p class="cf__multibuy${applied ? ' cf__multibuy--applied' : ''}" data-multibuy${hidden ? ' hidden' : ''} role="status">${html}</p>`;
  }

  /** Optional fit-guarantee opt-in card. Empty when the add-on is disabled in config. */
  #guaranteeToggle() {
    const cfg = CONFIG.fitGuarantee;
    if (!cfg || cfg.enabled === false) return '';
    const on = this.#order.fitGuarantee === true;
    const amount = fitGuaranteeAmount(priceOrder(this.#order).subtotal);
    return `
      <div class="cf__guarantee" data-guarantee>
        <button type="button" class="cf__guarantee-toggle" data-action="toggle-guarantee" aria-pressed="${on}">
          <span class="cf__guarantee-box" aria-hidden="true">${CHECK}</span>
          <span class="cf__guarantee-text">
            <span class="cf__guarantee-title">
              ${escapeHtml(cfg.label)}
              <span class="cf__guarantee-price" data-guarantee-amount>+${formatMoney(amount)}</span>
            </span>
            <span class="cf__guarantee-blurb">${escapeHtml(cfg.blurb)}</span>
          </span>
        </button>
        ${this.#guaranteeTerms(cfg)}
      </div>`;
  }

  /** Has the customer acknowledged the made-to-measure cancellation note? True when
   *  the notice is switched off in config, so the gate can't strand an order. */
  #consentOk() {
    const cfg = CONFIG.madeToMeasure;
    if (!cfg || cfg.enabled === false) return true;
    return this.#consentMadeToMeasure === true;
  }

  /**
   * REQUIRED made-to-measure acknowledgement (CCR 2013 reg 28(1)(b)) — the 14-day
   * right to cancel doesn't apply to goods made to the customer's specifications,
   * but we must say so before they buy. Blocks add-to-cart; the acknowledgement
   * then rides to the order line (see toLineItemProperties).
   */
  #consentToggle() {
    const cfg = CONFIG.madeToMeasure;
    if (!cfg || cfg.enabled === false) return '';
    const on = this.#consentMadeToMeasure;
    const link = cfg.policyUrl
      ? `<a class="cf__guarantee-terms-link" href="${escapeHtml(cfg.policyUrl)}">${escapeHtml(cfg.policyLabel ?? 'Returns policy')}</a>`
      : '';
    return `
      <div class="cf__consent" data-consent>
        <button type="button" class="cf__guarantee-toggle cf__consent-toggle" data-action="toggle-consent" aria-pressed="${on}">
          <span class="cf__guarantee-box" aria-hidden="true">${CHECK}</span>
          <span class="cf__guarantee-text">
            <span class="cf__guarantee-title">
              ${escapeHtml(cfg.label)}
              <span class="cf__consent-required">Required</span>
            </span>
            <span class="cf__guarantee-blurb">${escapeHtml(cfg.blurb)}</span>
          </span>
        </button>
        ${link}
      </div>`;
  }

  /** "What's covered" disclosure under the guarantee toggle (native <details>, no JS). */
  #guaranteeTerms(cfg) {
    const points = Array.isArray(cfg.terms) ? cfg.terms.filter(Boolean) : [];
    if (!points.length) return '';
    const items = points.map((t) => `<li>${escapeHtml(t)}</li>`).join('');
    const link = cfg.termsUrl
      ? `<a class="cf__guarantee-terms-link" href="${escapeHtml(cfg.termsUrl)}">Full terms</a>`
      : '';
    return `
      <details class="cf__guarantee-terms">
        <summary class="cf__guarantee-terms-summary">What’s covered</summary>
        <ul class="cf__guarantee-terms-list">${items}</ul>
        ${link}
      </details>`;
  }

  #feedbackMarkup() {
    return `
      <div class="cf__feedback">
        <span class="cf__validity" data-validity role="status" hidden></span>
        <span class="cf__cart-status" data-cart-status role="status" aria-live="polite" hidden></span>
      </div>`;
  }

  /** Small reassurance line near the add-to-cart (eases measuring anxiety). */
  #reassuranceMarkup() {
    const copy = CONFIG.guidance?.reassurance;
    if (!copy) return '';
    return `<p class="cf__reassure">${escapeHtml(copy)}</p>`;
  }

  /**
   * Optional "why aren't you ordering?" chips under the set total on the priced
   * review. All copy is config-driven (CONFIG.objections). Once a reason is tapped
   * the row collapses to a thank-you (with an optional per-reason follow-up), so a
   * customer can't spam it. Reset per build. See the config block for the rationale.
   */
  #objectionMarkup() {
    const cfg = CONFIG.objections;
    if (!cfg?.enabled || !cfg.reasons?.length) return '';
    if (this.#objection) {
      const reason = cfg.reasons.find((r) => r.id === this.#objection);
      const thanks = reason?.thanks ?? cfg.thanks ?? 'Thanks — that helps.';
      return `<div class="cf__objection cf__objection--done" data-objection role="status">
          <span class="cf__objection-thanks">${escapeHtml(thanks)}</span>
        </div>`;
    }
    const chips = cfg.reasons
      .map(
        (r) =>
          `<button type="button" class="cf__objection-chip" data-action="objection" data-reason="${escapeHtml(r.id)}">${escapeHtml(r.label)}</button>`,
      )
      .join('');
    return `<div class="cf__objection" data-objection>
        <span class="cf__objection-prompt">${escapeHtml(cfg.prompt ?? '')}</span>
        <div class="cf__objection-chips">${chips}</div>
      </div>`;
  }

  /** Record a tapped objection reason: fire the `cf_objection` event, push it onto
   *  the anonymous beacon, and collapse the chips to a thank-you IN PLACE (a full
   *  re-render would needlessly rebuild the review). One reason per build. */
  #recordObjection(reason) {
    const valid = (CONFIG.objections?.reasons ?? []).some((r) => r.id === reason);
    if (!valid || this.#objection) return; // unknown id or already answered
    this.#objection = reason;
    track('cf_objection', { ...this.#buildAnalytics(), reason });
    this.#scheduleAbandonBeacon({ immediate: true }); // the "why", alongside the "where"
    const block = this.querySelector('[data-objection]');
    if (block) block.outerHTML = this.#objectionMarkup();
  }

  /**
   * @param {Piece} piece
   * @param {number} index
   */
  #pieceCard(piece, index) {
    const removable = this.#order.pieces.length > 1;
    const collapsed = this.#collapsed.has(piece.id);
    return `
      <div class="cf__piece${collapsed ? ' cf__piece--collapsed' : ''}" data-piece-id="${piece.id}">
        <div class="cf__piece-head">
          <button type="button" class="cf__toggle" data-action="toggle"
                  aria-expanded="${collapsed ? 'false' : 'true'}" aria-label="Expand or collapse">
            ${CARET}
          </button>
          ${this.#nameField(piece, index)}
          <span class="cf__piece-summary" data-summary></span>
          ${this.#pieceActions(removable)}
        </div>
        <div class="cf__piece-body">
          <div class="cf__controls">${this.#controls(piece)}</div>
          ${this.#previewBlock()}
        </div>
        ${this.#pieceFoot(piece, true)}
      </div>`;
  }

  /** Editable cushion name. Empty falls back to "Piece N" everywhere. */
  #nameField(piece, index) {
    return `<input class="cf__name" type="text" data-field="label" value="${escapeHtml(piece.label ?? '')}"
              placeholder="Custom cushion" aria-label="Cushion name" />`;
  }

  /** Display title with the quantity baked in — "Back cushions ×3" / "Custom
   *  cushion ×1" — so the pre-loaded quantity is obvious the moment a starter
   *  loads. The editable name field still holds the base label; this is derived. */
  #pieceTitle(piece) {
    const base = piece.label && piece.label.trim() ? piece.label.trim() : 'Custom cushion';
    const noun = piece.quantity === 1 || /s$/i.test(base) ? base : `${base}s`;
    return `${noun} ×${piece.quantity}`;
  }

  #pieceActions(removable) {
    return `
      <div class="cf__piece-actions">
        <button type="button" class="cf__btn" data-action="duplicate">Duplicate</button>
        <button type="button" class="cf__btn cf__btn--danger" data-action="remove" ${removable ? '' : 'disabled'}>Remove</button>
      </div>`;
  }

  #pieceFoot(piece, withQty = false) {
    // With a quantity stepper present, show the line total (unit × qty); without
    // one, show the per-cushion price, so we never reference a quantity that
    // isn't on screen.
    const label = withQty ? 'Piece total' : 'Per cushion';
    const mode = withQty ? 'line' : 'unit';
    return `
      <div class="cf__piece-foot">
        ${withQty ? this.#qtyStepper(piece) : '<span></span>'}
        <span class="cf__line">
          <span class="cf__line-label">${label}</span>
          <span class="cf__line-value" data-line-total="${mode}">—</span>
        </span>
      </div>`;
  }

  /** Explicit "how many" stepper. Quantity is a deliberate choice, not a buried field. */
  #qtyStepper(piece) {
    const { min, max } = CONFIG.quantity;
    const q = piece.quantity;
    return `
      <span class="cf__qty" data-qty>
        <span class="cf__qty-label">Quantity</span>
        <span class="cf__qty-controls">
          <button type="button" class="cf__qty-btn" data-action="qty-dec" aria-label="Fewer" ${q <= min ? 'disabled' : ''}>&minus;</button>
          <span class="cf__qty-value" data-qty-value aria-live="polite">${q}</span>
          <button type="button" class="cf__qty-btn" data-action="qty-inc" aria-label="More" ${q >= max ? 'disabled' : ''}>+</button>
        </span>
      </span>`;
  }

  /** The grouped control fieldsets — shared by single cards and wizard steps. */
  #controls(piece) {
    const shape = shapeById(piece.shape);
    // Offer the selectable shapes, plus the piece's current shape if it's a
    // now-retired one (e.g. an old L-shape token) so it still shows as pressed.
    const shapeOptions = SELECTABLE_SHAPES.some((s) => s.id === piece.shape)
      ? SELECTABLE_SHAPES
      : [...SELECTABLE_SHAPES, shape];
    return `
      <fieldset class="cf__group">
        <legend class="cf__group-title">Shape &amp; size</legend>
        ${CONFIG.guidance?.size ? `<p class="cf__group-help">${CONFIG.guidance.size}</p>` : ''}
        ${this.#shapePicker(piece, shapeOptions)}
        <div class="cf__dims-head">
          <span class="cf__dims-title">Measurements</span>
          ${this.#unitToggle()}
        </div>
        <div class="cf__dims">
          ${shape.fields.map((fd) => this.#dimField(fd, piece.dimensions[fd.key])).join('')}
          ${this.#dimField(DEPTH_FIELD, piece.dimensions[DEPTH_FIELD.key])}
        </div>
        <div class="cf__nudges" data-nudges></div>
        ${
          shape.measure
            ? `<details class="cf__measure">
                 <summary class="cf__measure-summary">How to measure</summary>
                 <div class="cf__measure-body">
                   <div class="cf__measure-diagram">${measureDiagram(shape.id)}</div>
                   <p class="cf__measure-text">${shape.measure}</p>
                 </div>
               </details>`
            : ''
        }
      </fieldset>

      ${this.#coverOnlyToggle(piece)}
      ${piece.coverOnly ? '' : this.#foamGroup(piece)}

      <fieldset class="cf__group">
        <legend class="cf__group-title">Finish</legend>
        ${this.#fabricGroup(piece)}
        ${this.#chipGroup('pipingId', 'Piping', CONFIG.piping, piece.pipingId)}
        ${this.#addonGroup(piece)}
        ${this.#finishNote()}
        ${this.#zipNoteField(piece)}
        ${shape.requiresTemplate ? this.#templateField(piece) : ''}
      </fieldset>`;
  }

  /** Zip position is derived (illustrative best-guess, drawn on the preview). Some
   *  chair pads / curves want it on a shorter side, so rather than model every
   *  case we let the customer request a spot in free text — set at the workshop. */
  #zipNoteField(piece) {
    const note = CONFIG.guidance?.zip;
    return `
      <div class="cf__field cf__zip-note">
        <label class="cf__label" for="zipnote-${piece.id}">Zip position <span class="cf__label-opt">(optional)</span></label>
        ${note ? `<p class="cf__hint">${escapeHtml(note)}</p>` : ''}
        <input
          id="zipnote-${piece.id}"
          type="text"
          class="cf__note-input"
          data-field="zipNote"
          maxlength="200"
          value="${escapeHtml(piece.zipNote ?? '')}"
          placeholder="e.g. zip on the short back edge"
        >
      </div>`;
  }

  /** Standard-construction note: wrap/netting included + the flap-over-zip. Not a
   *  control — a reassurance that what used to look like paid extras is standard. */
  #finishNote() {
    const lines = [CONFIG.guidance?.finish, CONFIG.guidance?.cover].filter(Boolean);
    if (!lines.length) return '';
    return `<div class="cf__finish-note">${lines.map((t) => `<p>${escapeHtml(t)}</p>`).join('')}</div>`;
  }

  /** "Just the cover" toggle — drops the foam (and its step) for this piece.
   *  Empty when the feature is disabled in config. */
  #coverOnlyToggle(piece) {
    const cfg = CONFIG.coverOnly;
    if (!cfg || cfg.enabled === false) return '';
    const on = piece.coverOnly === true;
    return `
      <div class="cf__cover-only" data-cover-only>
        <button type="button" class="cf__cover-toggle" data-action="toggle-cover-only" aria-pressed="${on}">
          <span class="cf__cover-box" aria-hidden="true">${CHECK}</span>
          <span class="cf__cover-text">
            <span class="cf__cover-title">${escapeHtml(cfg.label)}</span>
            <span class="cf__cover-blurb">${escapeHtml(cfg.blurb)}</span>
          </span>
        </button>
        ${on && cfg.note ? `<p class="cf__cover-note">${escapeHtml(cfg.note)}</p>` : ''}
      </div>`;
  }

  /**
   * Shared scaffold for the two exposure-ranked option groups (foam + fabric):
   * descriptor-led tiles, a "Recommended" badge on the exposure's pick, an
   * advisory note slot and a care/detail expander. Only the per-option tile
   * body differs (fabric adds a swatch) — everything else must stay
   * behaviourally identical for the exposure UX to feel coherent, so it lives
   * here once. A third exposure-ranked family = one more call site.
   */
  #optionTileGroup({ label, field, options, selectedId, recommendedId, groupClass, tileClass, noteAttr, detailTitle, tileBody, tileAttrs = () => '', footnote = '' }) {
    const tiles = options
      .map(
        (o) => `<button type="button" class="${tileClass}" data-action="select" data-field="${field}"
                  data-value="${o.id}" aria-pressed="${o.id === selectedId}"${tileAttrs(o)}>${tileBody(o, recBadge(o.id, recommendedId))}</button>`
      )
      .join('');
    const detail = options
      .map((o) => `<li><b>${escapeHtml(o.label)}</b> — ${escapeHtml(o.help ?? '')}</li>`)
      .join('');
    return `
      <div class="cf__field">
        <span class="cf__label">${label}</span>
        <div class="${groupClass}" data-chips role="group" aria-label="${label}">${tiles}</div>
        <p class="cf__foam-note" data-${noteAttr} hidden></p>
        ${footnote ? `<p class="cf__option-footnote">${footnote}</p>` : ''}
        <details class="cf__foam-detail">
          <summary class="cf__foam-detail-summary">${detailTitle}</summary>
          <ul class="cf__foam-detail-list">${detail}</ul>
        </details>
      </div>`;
  }

  /**
   * Foam group, reframed around water handling. Each option LEADS with its
   * one-line water descriptor (always visible); the foam the stated exposure
   * recommends is badged; a note shows the recommendation reason — or, if the
   * customer picks a lighter foam than their location suggests, a soft nudge
   * framed around that location. Deeper detail (care/composition) sits behind an
   * expand — pulled, not pushed.
   */
  #foamGroup(piece) {
    const group = this.#optionTileGroup({
      label: 'Foam',
      field: 'foamId',
      options: visibleFoams(),
      selectedId: piece.foamId,
      recommendedId: exposureById(this.#order.exposure).foamId,
      groupClass: 'cf__foams',
      tileClass: 'cf__foam',
      noteAttr: 'foam-note',
      detailTitle: 'Foam care &amp; detail',
      tileBody: (f, rec) => `
                  <span class="cf__foam-head"><span class="cf__foam-name">${escapeHtml(f.label)}</span>${rec}</span>
                  <span class="cf__foam-water">${escapeHtml(f.water ?? '')}</span>`,
    });
    return `
      <fieldset class="cf__group">
        <legend class="cf__group-title">Comfort &amp; durability</legend>
        ${this.#exposureChooser()}
        ${group}
      </fieldset>`;
  }

  /** Optional extras — multi-select toggle chips (each adds a flat cost). */
  #addonGroup(piece) {
    const selected = new Set(piece.addonIds ?? []);
    const chips = CONFIG.addons
      .map(
        (a) => `<button type="button" class="cf__chip cf__chip--addon" data-action="toggle-addon"
                  data-addon="${a.id}" aria-pressed="${selected.has(a.id)}">${escapeHtml(a.label)}
                  <span class="cf__chip-price">+${formatMoney(a.costGBP)}</span></button>`
      )
      .join('');
    return `
      <div class="cf__field">
        <span class="cf__label">Optional extras</span>
        <div class="cf__chips" data-chips role="group" aria-label="Optional extras">${chips}</div>
      </div>`;
  }

  #previewBlock() {
    return `
      <div class="cf__preview">
        <div class="cf__preview-pane">
          <span class="cf__pane-label">Top view</span>
          <div data-preview="top"></div>
        </div>
        <div class="cf__preview-pane">
          <span class="cf__pane-label">Side profile</span>
          <div data-preview="side"></div>
        </div>
        <p class="cf__preview-note">The dashed line marks the zip — our suggested position (see “Zip position” below).</p>
      </div>`;
  }

  /* ---- wizard ---------------------------------------------------------- */

  /* Wizard step layout: steps 0..n−1 = pieces (piece index = step), step n =
     review. (The order-level "where will these live?" exposure choice is folded
     into each piece's foam group — see #exposureChooser — so it's no longer its
     own step.) These helpers are the single source of the step↔piece mapping IN
     BOTH DIRECTIONS — never hand-write the offset. */
  #stepKind(step = this.#step) {
    if (step < this.#order.pieces.length) return 'piece';
    return 'review';
  }
  #pieceAtStep(step = this.#step) {
    return this.#order.pieces[step];
  }
  /** Inverse of #pieceAtStep: the wizard step that edits piece index `i`. */
  #stepForPieceIndex(i) {
    return i;
  }
  #stepForPiece(piece) {
    return this.#stepForPieceIndex(this.#order.pieces.indexOf(piece));
  }
  #reviewStepIndex() {
    return this.#order.pieces.length;
  }

  #renderWizard() {
    this.#step = this.#clampStep(this.#step);
    const kind = this.#stepKind();
    const stage = kind === 'review' ? this.#reviewStep() : this.#pieceStep(this.#pieceAtStep(), this.#step);
    return `
      <div class="cf cf--wizard">
        ${this.#topBar()}
        ${this.#stepper()}
        <div class="cf__stage" tabindex="-1">
          ${stage}
        </div>
        <div class="cf__nav">
          <button type="button" class="cf__add" data-action="back" ${this.#step === 0 ? 'disabled' : ''}>Back</button>
          ${this.#needsQuote() ? this.#quotePriceNote() : this.#totalMarkup()}
          ${
            kind === 'review'
              ? this.#checkoutControl()
              : `<button type="button" class="cf__checkout" data-action="next">Next</button>`
          }
          ${kind === 'review' ? this.#reassuranceMarkup() : ''}
        </div>
        ${this.#feedbackMarkup()}
      </div>`;
  }

  /** Order-level "where will these live?" chooser, folded into the foam group so
   *  it frames + recommends the foam without its own wizard step. Changing it
   *  re-recommends the foam on every piece (see #onSelectExposure). */
  #exposureChooser() {
    return `
      <div class="cf__exposure cf__exposure--inline">
        <span class="cf__label">Where will these live?</span>
        <p class="cf__exposure-sub">This decides how long they last outdoors — we’ll suggest the right foam.</p>
        ${this.#exposureCards()}
      </div>`;
  }

  /** The three exposure choice cards (shared by wizard step + single-flow panel). */
  #exposureCards() {
    const current = this.#order.exposure;
    const cards = CONFIG.exposures
      .map(
        (e) => `
        <button type="button" class="cf__exposure-card" data-action="select-exposure" data-value="${e.id}" aria-pressed="${e.id === current}">
          <span class="cf__exposure-label">${escapeHtml(e.label)}</span>
          <span class="cf__exposure-desc">${escapeHtml(e.sublabel)}</span>
        </button>`
      )
      .join('');
    return `<div class="cf__exposure-cards" data-exposure role="group" aria-label="Where will these cushions live?">${cards}</div>`;
  }

  #stepper() {
    const pieces = this.#order.pieces;
    const pips = [];
    // Piece pips (steps 0..n−1).
    pieces.forEach((p, i) => {
      const step = this.#stepForPieceIndex(i);
      const label = escapeHtml(this.#pieceTitle(p));
      const invalid = !validatePiece(p).valid;
      pips.push(`<li><button type="button" class="cf__step${step === this.#step ? ' is-current' : ''}${invalid ? ' is-invalid' : ''}"
                data-action="goto" data-step="${step}" ${step === this.#step ? 'aria-current="step"' : ''}>${label}</button></li>`);
    });
    // Add-a-cushion pip, reachable mid-flow without going to Review.
    pips.push(
      `<li><button type="button" class="cf__step cf__step--add" data-action="add" aria-label="Add a cushion">+</button></li>`
    );
    const reviewIdx = this.#reviewStepIndex();
    pips.push(
      `<li><button type="button" class="cf__step${this.#step >= reviewIdx ? ' is-current' : ''}"
        data-action="goto" data-step="${reviewIdx}" ${this.#step >= reviewIdx ? 'aria-current="step"' : ''}>Review</button></li>`
    );
    return `<ol class="cf__steps">${pips.join('')}</ol>`;
  }

  #pieceStep(piece, index) {
    const removable = this.#order.pieces.length > 1;
    return `
      <div class="cf__piece cf__piece--step" data-piece-id="${piece.id}">
        <div class="cf__piece-head cf__piece-head--step">
          ${this.#nameField(piece, index)}
          ${this.#pieceActions(removable)}
        </div>
        <div class="cf__piece-body">
          <div class="cf__controls">${this.#controls(piece)}</div>
          ${this.#previewBlock()}
        </div>
        ${this.#pieceFoot(piece, true)}
      </div>`;
  }

  #reviewStep() {
    const rows = this.#order.pieces
      .map((p, i) => {
        const removable = this.#order.pieces.length > 1;
        const fabric = optionById(CONFIG.fabrics, p.fabricId);
        return `
        <li class="cf__review-row" data-piece-id="${p.id}">
          <span class="cf__review-swatch" style="--sw:${fabric.swatch}" title="${escapeHtml(fabric.label)}" aria-hidden="true"></span>
          <div class="cf__review-info">
            <span class="cf__review-name">${escapeHtml(this.#pieceTitle(p))}</span>
            <span class="cf__review-spec" data-review-spec>${escapeHtml(this.#pieceSummary(p))}</span>
          </div>
          ${this.#qtyStepper(p)}
          <div class="cf__review-actions">
            <button type="button" class="cf__btn" data-action="goto" data-step="${this.#stepForPieceIndex(i)}">Edit</button>
            <button type="button" class="cf__btn cf__btn--danger" data-action="remove" ${removable ? '' : 'disabled'}>Remove</button>
          </div>
        </li>`;
      })
      .join('');
    const exposure = exposureById(this.#order.exposure);
    return `
      <div class="cf__review">
        <div class="cf__review-head">
          <h3 class="cf__review-title">Your set</h3>
          <button type="button" class="cf__review-exposure" data-action="goto" data-step="0" title="Change where these will be used">${escapeHtml(exposure.reviewLabel)}</button>
        </div>
        <ul class="cf__review-list">${rows}</ul>
        <button type="button" class="cf__add" data-action="add">+ Add another piece</button>
        ${this.#needsQuote() ? '' : this.#multiBuyNudge()}
        ${this.#needsQuote() ? '' : this.#deliveryEstimateMarkup()}
        ${this.#needsQuote() ? '' : this.#sensecheckMarkup()}
        ${this.#needsQuote() ? this.#quoteForm() : this.#guaranteeToggle()}
        ${this.#needsQuote() ? '' : this.#consentToggle()}
        ${this.#needsQuote() ? '' : this.#objectionMarkup()}
      </div>`;
  }

  /** Estimated delivery window for the current build. Made-to-order, so the
   *  estimate reflects real component availability (foam runs biweekly; a missed
   *  cutoff waits for the next). Pure #leadtime module; instant-price builds only
   *  (bespoke quotes carry their own timeline). */
  #deliveryEstimateMarkup() {
    let est;
    try {
      est = estimateDelivery(this.#order);
    } catch {
      return '';
    }
    const why = est.needsFoam
      ? `Made for our next foam run (${est.foamRunLabel}), then made up and shipped`
      : 'Cover only — no foam wait, made up and shipped';
    const orderBy =
      est.needsFoam && est.orderByLabel
        ? ` Order by <strong>${escapeHtml(est.orderByLabel)}</strong> to make this run.`
        : '';
    return `
      <div class="cf__leadtime" data-leadtime>
        <span class="cf__leadtime-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l9-4 9 4v10l-9 4-9-4z"/><path d="M3 7l9 4 9-4M12 11v10"/></svg>
        </span>
        <div class="cf__leadtime-body">
          <span class="cf__leadtime-est">Estimated delivery <strong>${escapeHtml(est.label)}</strong></span>
          <span class="cf__leadtime-why">${escapeHtml(why)}.${orderBy}</span>
        </div>
      </div>`;
  }

  /** Optional "sense-check my order" photo dropzone (instant-price builds only).
   *  Photos are VIEW STATE; on add-to-cart they ride to the team via /sensecheck
   *  so a human can check the measurements against the customer's current set. */
  #sensecheckMarkup() {
    const copy = CONFIG.sensecheck;
    if (!copy?.enabled) return '';
    const max = copy.maxPhotos ?? MAX_QUOTE_PHOTOS;
    const shots = this.#sensecheckPhotos;
    const thumbs = shots
      .map(
        (s, i) => `
        <div class="cf__sc-thumb">
          <img src="${s.url}" alt="${escapeHtml(s.file.name || 'photo')}" />
          <button type="button" class="cf__sc-remove" data-action="remove-sensecheck" data-sc-index="${i}" aria-label="Remove photo">×</button>
        </div>`
      )
      .join('');
    const atCap = shots.length >= max;
    const adder = atCap
      ? `<p class="cf__sc-hint">That's the maximum of ${max} photos. Remove one to add another.</p>`
      : `<label class="cf__sc-drop">
           <input type="file" accept="image/*" multiple data-field="sensecheck-photos" />
           <span class="cf__sc-drop-icon" aria-hidden="true">
             <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>
           </span>
           <span class="cf__sc-drop-main">Add photos</span>
           <span class="cf__sc-hint">${escapeHtml(copy.hint)}</span>
         </label>`;
    return `
      <div class="cf__sensecheck" data-sensecheck>
        <div class="cf__sc-head">
          <span class="cf__sc-title">${escapeHtml(copy.title)} <span class="cf__sc-opt">(${escapeHtml(copy.optionalLabel || 'optional')})</span></span>
          <p class="cf__sc-body">${escapeHtml(copy.body)}</p>
        </div>
        ${thumbs ? `<div class="cf__sc-thumbs">${thumbs}</div>` : ''}
        ${adder}
      </div>`;
  }

  /** Re-render just the sense-check block in place (adding/removing a photo
   *  shouldn't disturb the rest of the review). */
  #refreshSensecheck() {
    const block = this.querySelector('[data-sensecheck]');
    if (block) block.outerHTML = this.#sensecheckMarkup();
  }

  /** Visual shape picker — each option is its own top-down silhouette + a label,
   *  so the customer self-selects from a picture of the shape, not a word. The
   *  shape's own `topDown` draws the thumbnail (silhouette mode: no dims/zip), so
   *  a new shape needs no extra picker code. Shape is structural → select re-renders. */
  #shapePicker(piece, shapes) {
    const tiles = shapes
      .map((s) => {
        const thumb = s.topDown(shapeDefaults(s), { silhouette: true });
        return `<button type="button" class="cf__shape-pick" data-action="select" data-field="shape"
                  data-value="${s.id}" aria-pressed="${s.id === piece.shape}" title="${escapeHtml(s.label)}">
                  <span class="cf__shape-thumb" aria-hidden="true">${thumb}</span>
                  <span class="cf__shape-name">${escapeHtml(s.label)}</span>
                </button>`;
      })
      .join('');
    return `
      <div class="cf__field">
        <span class="cf__label">Shape</span>
        <div class="cf__shapes" data-chips role="group" aria-label="Shape">${tiles}</div>
      </div>`;
  }

  /** A row of pill buttons for a choice field. `options` are {id,label}. (No
   *  recommendation badge here — exposure-recommended families use
   *  #optionTileGroup, which owns the badge + note behaviour.) */
  #chipGroup(field, label, options, selected) {
    const chips = options
      .map(
        (o) => `<button type="button" class="cf__chip" data-action="select" data-field="${field}"
                  data-value="${o.id}" aria-pressed="${o.id === selected}">${escapeHtml(o.label)}</button>`
      )
      .join('');
    return `
      <div class="cf__field">
        <span class="cf__label">${label}</span>
        <div class="cf__chips" data-chips role="group" aria-label="${label}">${chips}</div>
        <p class="cf__option-help" data-help-for="${field}"></p>
      </div>`;
  }

  /**
   * Fabric group — visual swatch tiles that, like foam, LEAD with a one-line
   * weather/UV descriptor (always visible) and badge the fabric the stated
   * exposure recommends. A note shows the recommendation reason — or, if a less
   * weatherproof fabric than the exposure suggests is picked, a location-framed
   * nudge. Advisory only: exposure never overwrites the customer's fabric (it's a
   * visible colour choice). Deeper detail sits behind an expand.
   */
  #fabricGroup(piece) {
    return this.#optionTileGroup({
      label: 'Fabric',
      field: 'fabricId',
      // Only the workhorse colours at launch; the tier ladder is hidden until
      // the guidance switch is on (which is also when a fabric gets an exposure
      // badge — recommending one weave-quality over another only makes sense with
      // more than one tier; between two colours it would just be noise).
      options: visibleFabrics(),
      selectedId: piece.fabricId,
      recommendedId: CONFIG.fabricExposureGuidance ? exposureById(this.#order.exposure).fabricId : null,
      groupClass: 'cf__fabrics',
      tileClass: 'cf__fabric',
      noteAttr: 'fabric-note',
      footnote: CONFIG.guidance?.fabricColours ?? '',
      detailTitle: 'Fabric care &amp; detail',
      tileAttrs: (f) => ` title="${escapeHtml(f.label)}"`,
      tileBody: (f, rec) => `
                  <span class="cf__swatch-chip" style="--sw:${f.swatch}"></span>
                  <span class="cf__fabric-body">
                    <span class="cf__fabric-head"><span class="cf__fabric-name">${escapeHtml(f.label)}</span>${rec}${f.quoteOnly ? '<span class="cf__fabric-badge">Back-order · by quote</span>' : ''}</span>
                    <span class="cf__foam-water">${escapeHtml(f.weather ?? '')}</span>
                  </span>`,
    });
  }

  /** Dimension field: a synced slider + number, both editing the same value.
   *  `valueMm` is canonical; the inputs render in the chosen display unit. */
  #dimField(def, valueMm) {
    const unit = this.#unit;
    const u = UNITS[unit];
    const { min, max, step } = fieldBounds(def, unit);
    const value = displayValue(valueMm, unit);
    const aria = `${def.label} in ${u.label}`;
    return `
      <div class="cf__field cf__dim">
        <span class="cf__label">${def.label} (${u.short})</span>
        <input type="number" class="cf__num" data-field="${def.key}"
               min="${min}" max="${max}" step="${step}" value="${value}" aria-label="${aria}" />
        <input type="range" class="cf__range" data-field="${def.key}"
               min="${min}" max="${max}" step="${step}" value="${value}" aria-label="${aria}" />
        <span class="cf__error" data-error="${def.key}"></span>
      </div>`;
  }

  /** cm / inches segmented toggle. Units are a display lens; the model stays mm. */
  #unitToggle() {
    const buttons = Object.values(UNITS)
      .map((u) => {
        const active = this.#unit === u.id;
        const label = u.id === 'in' ? 'inches' : u.short;
        return `<button type="button" class="cf__unit${active ? ' is-active' : ''}"
                  data-action="set-unit" data-unit="${u.id}" aria-pressed="${active}">${label}</button>`;
      })
      .join('');
    return `<div class="cf__units" role="group" aria-label="Measurement units">${buttons}</div>`;
  }

  #templateField(piece) {
    const files = this.#templates.get(piece.id) ?? [];
    const thumbs = files
      .map(
        (t, i) => `
        <div class="cf__template">
          <img class="cf__template-thumb" src="${t.url}" alt="${escapeHtml(t.name)}" />
          <span class="cf__template-name">${escapeHtml(t.name)}</span>
          <button type="button" class="cf__btn cf__btn--danger" data-action="remove-template" data-template-index="${i}">Remove</button>
        </div>`
      )
      .join('');
    // Cap is across the WHOLE build (matches the email attachment limit), so a
    // second bespoke piece shares the same budget.
    const atCap = this.#totalTemplateCount() >= MAX_QUOTE_PHOTOS;
    const adder = atCap
      ? `<span class="cf__hint">That's the maximum of ${MAX_QUOTE_PHOTOS} photos. Remove one to add another.</span>`
      : `<input type="file" accept="image/*" multiple data-field="template" />
         <span class="cf__hint">Optional, but a photo or paper template really helps us quote accurately (up to ${MAX_QUOTE_PHOTOS}) — you can also send one when we reply. We'll confirm the price against it.</span>`;
    return `
      <div class="cf__field">
        <span class="cf__label">Template / photos <span class="cf__label-opt">(optional)</span></span>
        ${files.length ? `<div class="cf__templates">${thumbs}</div>` : ''}
        ${adder}
        <span class="cf__error" data-error="template"></span>
      </div>`;
  }

  /** Total reference photos across every piece (the build-wide cap is global). */
  #totalTemplateCount() {
    let n = 0;
    for (const list of this.#templates.values()) n += list.length;
    return n;
  }

  /* ---- events (delegated from root) ------------------------------------ */

  #bindEvents() {
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onInput);
    this.addEventListener('click', this.#onClick);
    // Don't lose the last ~300ms of edits (debounced draft) when the tab closes.
    window.addEventListener('pagehide', this.#flushDraft);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    window.removeEventListener('pagehide', this.#flushDraft);
    this.#flushDraft();
    this.#disposeSensecheck(); // revoke any preview object URLs
  }

  #onInput = (event) => {
    const el = event.target.closest('[data-field]');
    if (!el) return;
    // 'change' re-fires with the same value after every committed 'input' edit —
    // skip it for everything except file inputs (the one control with no 'input').
    if (event.type === 'change' && el.type !== 'file') return;

    // Order-level sense-check photos live in the review, not inside a piece — so
    // handle them before the per-piece lookup (which would bail with no piece).
    if (el.dataset.field === 'sensecheck-photos') {
      return this.#onSensecheckSelected(el);
    }

    const piece = this.#pieceFromNode(el);
    if (!piece) return;
    const field = el.dataset.field;

    // Renaming: update in place (price/preview unaffected); sync the stepper pip.
    if (field === 'label') {
      piece.label = el.value.trim() === '' ? null : el.value;
      const pip = this.querySelector(`.cf__step[data-step="${this.#stepForPiece(piece)}"]`);
      if (pip) pip.textContent = this.#pieceTitle(piece);
      return this.#refreshTotal(); // price unchanged, but this is the draft-save chokepoint
    }

    // Zip-position request: free text, no price/preview/validity impact — update in
    // place and let the draft-save chokepoint persist it.
    if (field === 'zipNote') {
      piece.zipNote = el.value;
      return this.#refreshTotal();
    }

    // Shape and template change which controls exist → full re-render.
    if (field === 'shape') {
      setPieceShape(piece, el.value);
      return this.#rerender();
    }
    if (field === 'template') {
      return this.#onTemplateSelected(piece, el);
    }

    if (field.endsWith('Mm')) {
      const value = parseFloat(el.value) || 0;
      piece.dimensions[field] = toMm(value, this.#unit);
      // A dimension edit is the "actually measuring their furniture" signal —
      // without it, a buyer who keeps the default shape and types in their real
      // sizes never advances past 'start' and the funnel reads as a bounce.
      this.#trackStage('cf_dimensions_entered', { shape: piece.shape });
      this.#markStage('dimensions');
      // Zip follows the longest seam, so resizing can move it (e.g. a square
      // becoming wide) — recompute before the preview redraws.
      piece.zipId = defaultZipEdge(piece.shape, piece.dimensions);
      // Keep the paired slider + number in sync without a re-render.
      const fieldEl = el.closest('.cf__field');
      if (fieldEl) {
        for (const input of fieldEl.querySelectorAll(`[data-field="${field}"]`)) {
          if (input !== el) input.value = value;
        }
      }
    } else {
      piece[field] = el.value;
    }

    this.#refreshPiece(el.closest('[data-piece-id]'), piece);
    this.#refreshTotal();
    this.#refreshValidity();
  };

  #onTemplateSelected(piece, input) {
    // Append, don't replace — and never exceed the build-wide photo budget
    // (silently dropping extras here would mislead; the adder hides at the cap).
    const remaining = MAX_QUOTE_PHOTOS - this.#totalTemplateCount();
    const incoming = [...(input.files ?? [])]
      .filter((f) => f.type?.startsWith('image/'))
      .slice(0, Math.max(0, remaining));
    if (incoming.length) {
      const list = this.#templates.get(piece.id) ?? [];
      for (const file of incoming) list.push({ file, url: URL.createObjectURL(file), name: file.name });
      this.#templates.set(piece.id, list);
      piece.templateRef = list[0].name; // primary photo → satisfies validation + the spec
    }
    this.#rerender();
  }

  #onSensecheckSelected(input) {
    const max = CONFIG.sensecheck?.maxPhotos ?? MAX_QUOTE_PHOTOS;
    const remaining = max - this.#sensecheckPhotos.length;
    const incoming = [...(input.files ?? [])]
      .filter((f) => f.type?.startsWith('image/'))
      .slice(0, Math.max(0, remaining));
    for (const file of incoming) {
      this.#sensecheckPhotos.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file, url: URL.createObjectURL(file) });
    }
    this.#refreshSensecheck();
  }

  /** Revoke the preview object URLs and drop the attached sense-check photos. */
  #disposeSensecheck() {
    for (const s of this.#sensecheckPhotos) URL.revokeObjectURL(s.url);
    this.#sensecheckPhotos = [];
  }

  #onClick = (event) => {
    const btn = event.target.closest('[data-action]');
    if (!btn) return;
    const action = btn.dataset.action;

    // Starter chooser (no order yet) — these don't need piece context.
    if (action === 'choose-starter') {
      this.#order = createOrder(btn.dataset.starter || null);
      this.#step = 0;
      this.#shareWarnings = null;
      this.#trackedStages = new Set();
      this.#consentMadeToMeasure = false; // every build is acknowledged afresh
      this.#objection = null;
      this.#disposeSensecheck();
      this.#clearBuildParam(); // a fresh build shouldn't keep the old link in the URL
      track('cf_configurator_start', { source: 'chooser', starter: btn.dataset.starter || 'scratch' });
      return this.#rerender();
    }
    if (action === 'restart') {
      this.#order = null;
      this.#step = 0;
      this.#shareWarnings = null;
      this.#pendingDraft = null;
      this.#trackedStages = new Set();
      this.#consentMadeToMeasure = false;
      this.#objection = null;
      this.#disposeSensecheck();
      this.#clearDraft(); // Start over = discard; don't re-offer it on the chooser
      this.#clearBuildParam();
      return this.#render(); // back to the chooser; nothing to refresh
    }
    if (action === 'resume-draft') {
      if (!this.#pendingDraft) return this.#render();
      this.#order = this.#pendingDraft.order;
      this.#pendingDraft = null;
      this.#step = 0;
      this.#trackedStages = new Set();
      this.#consentMadeToMeasure = false; // a resumed draft must be re-acknowledged
      this.#objection = null;
      track('cf_configurator_start', { source: 'resume' });
      return this.#rerender(); // re-saves the now-live design (same content)
    }
    if (action === 'discard-draft') {
      this.#pendingDraft = null;
      this.#clearDraft();
      return this.#render();
    }
    if (action === 'set-unit') {
      const next = asUnit(btn.dataset.unit);
      if (next === this.#unit) return;
      this.#unit = next;
      try {
        localStorage.setItem('cf-unit', next);
      } catch {
        /* storage blocked — the choice just won't persist */
      }
      return this.#order ? this.#rerender() : this.#render();
    }
    if (action === 'dismiss-notice') {
      this.#shareWarnings = null;
      return this.#order ? this.#rerender() : this.#render();
    }
    if (!this.#order) return; // chooser is showing; ignore stray clicks

    const pieces = this.#order.pieces;

    if (action === 'add') {
      const piece = createPiece();
      piece.foamId = exposureById(this.#order.exposure).foamId; // follow the stated exposure
      pieces.push(piece);
      // In the wizard, jump to the new piece's step to configure it.
      if (this.#flow === 'wizard') this.#step = this.#stepForPieceIndex(pieces.length - 1);
      return this.#rerender();
    }
    if (action === 'add-to-cart') {
      return this.#handleAddToCart();
    }
    if (action === 'remove-sensecheck') {
      const i = Number(btn.dataset.scIndex);
      const [removed] = this.#sensecheckPhotos.splice(i, 1);
      if (removed) URL.revokeObjectURL(removed.url);
      return this.#refreshSensecheck();
    }
    if (action === 'submit-quote') {
      return this.#submitQuote(btn);
    }
    if (action === 'objection') {
      return this.#recordObjection(btn.dataset.reason);
    }
    if (action === 'share') {
      return this.#shareDesign(btn);
    }
    if (action === 'toggle-email-design') {
      this.#emailDesignOpen = !this.#emailDesignOpen;
      this.#rerender();
      if (this.#emailDesignOpen) this.querySelector('[data-email-design-input]')?.focus();
      return;
    }
    if (action === 'send-design-email') {
      return this.#submitDesignEmail(btn);
    }
    if (action === 'save-design') {
      return this.#saveToLibrary(btn);
    }
    if (action === 'toggle-guarantee') {
      this.#order.fitGuarantee = !this.#order.fitGuarantee;
      return this.#refreshTotal(); // updates the toggle, the guarantee line and the total
    }
    if (action === 'toggle-consent') {
      this.#consentMadeToMeasure = !this.#consentMadeToMeasure;
      btn.setAttribute('aria-pressed', String(this.#consentMadeToMeasure));
      return this.#refreshValidity(); // gates add-to-cart
    }
    // Order-level actions (no piece context needed).
    if (action === 'select-exposure') return this.#onSelectExposure(btn);
    // Wizard navigation.
    if (action === 'back') return this.#goToStep(this.#step - 1);
    if (action === 'goto') return this.#goToStep(parseInt(btn.dataset.step, 10));
    if (action === 'next') {
      if (this.#stepKind() === 'piece') {
        const current = this.#pieceAtStep();
        if (current && !validatePiece(current).valid) {
          // Block advancing; surface this piece's inline errors and focus the first.
          const card = this.querySelector(`[data-piece-id="${current.id}"]`);
          this.#refreshPiece(card, current);
          card?.querySelector('.cf__input--invalid')?.focus();
          return;
        }
      }
      return this.#goToStep(this.#step + 1);
    }

    const id = btn.closest('[data-piece-id]')?.dataset.pieceId;
    const index = pieces.findIndex((p) => p.id === id);
    if (index === -1) return;

    switch (action) {
      case 'select':
        return this.#onSelect(btn, pieces[index]);
      case 'qty-inc':
        return this.#changeQty(btn, pieces[index], +1);
      case 'qty-dec':
        return this.#changeQty(btn, pieces[index], -1);
      case 'toggle-addon':
        return this.#onToggleAddon(btn, pieces[index]);
      case 'toggle-cover-only':
        // Flips which controls exist (foam group appears/disappears) → re-render.
        pieces[index].coverOnly = !pieces[index].coverOnly;
        return this.#rerender();
      case 'toggle':
        return this.#toggleCollapsed(id);
      case 'duplicate': {
        const source = pieces[index];
        const dup = clonePiece(source);
        if (source.label) dup.label = `${source.label} (copy)`;
        pieces.splice(index + 1, 0, dup);
        const srcTemplates = this.#templates.get(id);
        if (srcTemplates?.length) {
          // Copying the photos counts against the build-wide budget too.
          const remaining = MAX_QUOTE_PHOTOS - this.#totalTemplateCount();
          const copied = srcTemplates
            .slice(0, Math.max(0, remaining))
            .map((t) => ({ file: t.file, url: URL.createObjectURL(t.file), name: t.name }));
          if (copied.length) this.#templates.set(dup.id, copied);
          dup.templateRef = copied[0]?.name ?? null;
        }
        if (this.#flow === 'wizard') this.#step = this.#stepForPieceIndex(index + 1); // land on the copy to rename it
        return this.#rerender();
      }
      case 'remove':
        if (pieces.length <= 1) return;
        pieces.splice(index, 1);
        this.#disposePiece(id);
        return this.#rerender();
      case 'remove-template': {
        const i = Number(btn.dataset.templateIndex);
        const list = this.#templates.get(id) ?? [];
        const [removed] = list.splice(i, 1);
        if (removed) URL.revokeObjectURL(removed.url);
        if (list.length) this.#templates.set(id, list);
        else this.#templates.delete(id);
        pieces[index].templateRef = list[0]?.name ?? null; // re-point to the new primary
        return this.#rerender();
      }
    }
  };

  /** Handle a chip/swatch selection. Shape is structural (re-render); others update in place. */
  #onSelect(btn, piece) {
    const { field, value } = btn.dataset;
    if (field === 'shape') {
      if (value === piece.shape) return;
      setPieceShape(piece, value);
      this.#trackStage('cf_shape_selected', { shape: value });
      this.#markStage('shape');
      return this.#rerender();
    }
    piece[field] = value;
    // Fabric/foam are the "made a real choice" signal — the funnel rung between
    // picking a shape and reaching the review.
    if (field === 'fabricId' || field === 'foamId') this.#markStage('options');
    for (const sibling of btn.closest('[data-chips]').querySelectorAll('[data-action="select"]')) {
      sibling.setAttribute('aria-pressed', String(sibling === btn));
    }
    // Only the zip choice is drawn on the preview; other selects leave geometry alone.
    this.#refreshPiece(btn.closest('[data-piece-id]'), piece, { geometry: field === 'zipId' });
    this.#refreshTotal();
    this.#refreshValidity();
  }

  /** Order-level exposure choice → re-recommend the matching foam on every piece. */
  #onSelectExposure(btn) {
    const id = btn.dataset.value;
    if (id === this.#order.exposure) return;
    applyExposureFoam(this.#order, id); // sets order.exposure + every piece.foamId
    // Exposure is folded into the foam group; re-render so the foam recommendation
    // (pressed tile, badge, note) and the total reflect the new location choice.
    this.#rerender();
  }

  /* ---- analytics (GA4 funnel via #analytics) --------------------------- */

  /** GA4-style params for the current order: value + item breakdown. */
  #buildAnalytics() {
    const order = this.#order;
    if (!order) return {};
    return {
      currency: CONFIG.currency,
      value: Number(priceOrder(order).total.toFixed(2)),
      piece_count: order.pieces.length,
      exposure: order.exposure,
      items: order.pieces.map((p) => {
        const shape = shapeById(p.shape);
        const fabric = optionById(CONFIG.fabrics, p.fabricId);
        const foam = optionById(CONFIG.foams, p.foamId);
        return {
          item_id: p.shape,
          item_name: `${shape?.label ?? p.shape} cushion`,
          item_category: p.coverOnly ? 'cover-only' : 'cushion',
          item_variant: p.coverOnly
            ? `${fabric?.label ?? p.fabricId} / cover only`
            : `${fabric?.label ?? p.fabricId} / ${foam?.label ?? p.foamId}`,
          price: Number(pricePiece(p).unitTotal.toFixed(2)),
          quantity: p.quantity,
        };
      }),
    };
  }

  /** Fire a funnel-stage event at most once per build, for a clean GA4 funnel. */
  #trackStage(name, params = {}) {
    if (this.#trackedStages.has(name)) return;
    this.#trackedStages.add(name);
    track(name, params);
  }

  /** Adjust a piece's quantity from a stepper (clamped to config limits), in place. */
  #changeQty(btn, piece, delta) {
    const { min, max } = CONFIG.quantity;
    const next = Math.max(min, Math.min(max, piece.quantity + delta));
    if (next === piece.quantity) return;
    piece.quantity = next;

    const container = btn.closest('[data-piece-id]');
    const valEl = container.querySelector('[data-qty-value]');
    if (valEl) valEl.textContent = String(next);
    const dec = container.querySelector('[data-action="qty-dec"]');
    const inc = container.querySelector('[data-action="qty-inc"]');
    if (dec) dec.disabled = next <= min;
    if (inc) inc.disabled = next >= max;

    // Re-price whatever price display this container has (piece foot or review row).
    this.#refreshPiece(container, piece, { geometry: false }); // updates [data-line-total]/[data-summary] if present
    const spec = container.querySelector('[data-review-spec]');
    if (spec) spec.textContent = this.#pieceSummary(piece);
    const name = container.querySelector('.cf__review-name'); // keep "… ×N" in sync
    if (name) name.textContent = this.#pieceTitle(piece);
    const pip = this.querySelector(`.cf__step[data-step="${this.#stepForPiece(piece)}"]`);
    if (pip) pip.textContent = this.#pieceTitle(piece); // and the stepper chip
    this.#refreshTotal();
  }

  /** Toggle an optional extra on a piece (multi-select) and re-price in place. */
  #onToggleAddon(btn, piece) {
    const id = btn.dataset.addon;
    const set = new Set(piece.addonIds ?? []);
    set.has(id) ? set.delete(id) : set.add(id);
    piece.addonIds = [...set];
    btn.setAttribute('aria-pressed', String(set.has(id)));
    this.#refreshPiece(btn.closest('[data-piece-id]'), piece, { geometry: false });
    this.#refreshTotal();
  }

  /** Flip collapsed state in place — toggles the class, no re-render. */
  #toggleCollapsed(id) {
    const card = this.querySelector(`[data-piece-id="${id}"]`);
    if (!card) return;
    const collapsed = card.classList.toggle('cf__piece--collapsed');
    if (collapsed) this.#collapsed.add(id);
    else this.#collapsed.delete(id);
    card.querySelector('.cf__toggle')?.setAttribute('aria-expanded', String(!collapsed));
  }

  /* ---- refresh --------------------------------------------------------- */

  #clampStep(n) {
    // 0..len−1 = pieces, len = review.
    return Math.max(0, Math.min(n, this.#order.pieces.length));
  }

  /** Move to a wizard step, re-render, and bring the new step into view + focus (a11y). */
  #goToStep(n) {
    this.#step = this.#clampStep(n);
    this.#rerender();
    if (this.#flow === 'wizard' && this.#stepKind() === 'review') {
      this.#trackStage('cf_review_reached', this.#buildAnalytics());
      this.#markStage('review');
      this.#scheduleAbandonBeacon({ immediate: true }); // a priced build is the useful signal
    }
    const stage = this.querySelector('.cf__stage');
    stage?.focus({ preventScroll: true });
    stage?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  #rerender() {
    this.#step = this.#clampStep(this.#step);
    this.#render();
    this.#refreshAll();
  }

  /**
   * Refresh a piece's card. `card` may be absent (wizard shows one piece / a review).
   * `geometry: false` skips the SVG preview rebuild + dimension nudges for edits
   * that can't change them (fabric/foam/piping swatch taps, quantity, add-ons) —
   * tearing down and re-parsing two SVG subtrees per swatch tap is visible
   * paint churn on the single-page flow.
   */
  #refreshPiece(card, piece, { geometry = true } = {}) {
    if (!card) return;
    const shape = shapeById(piece.shape);
    if (geometry) {
      const top = card.querySelector('[data-preview="top"]');
      const side = card.querySelector('[data-preview="side"]');
      if (top) top.innerHTML = shape.topDown(piece.dimensions, { zip: piece.zipId, unit: this.#unit });
      if (side) {
        side.innerHTML = sideProfileSvg(shape.spanMm(piece.dimensions), piece.dimensions[DEPTH_FIELD.key], { unit: this.#unit });
      }

      // Smart nudges: advisory notes for unusual (but valid) values, gathered
      // into one area below the dimensions so they never disturb the input row.
      const nudgeBox = card.querySelector('[data-nudges]');
      if (nudgeBox) {
        nudgeBox.innerHTML = Object.values(nudgesForPiece(piece))
          .map((message) => `<p class="cf__nudge">${escapeHtml(message)}</p>`)
          .join('');
      }
    }

    const pr = pricePiece(piece);
    // A bespoke (template-required) piece is priced by manual quote, not the
    // ladder — its computed cost is only a bounding-box estimate, so don't show a
    // firm number that the quote may not match.
    const quoteOnly = shapeById(piece.shape).requiresTemplate;
    const line = card.querySelector('[data-line-total]');
    if (line) {
      if (quoteOnly) {
        line.textContent = 'By quote';
      } else if (line.dataset.lineTotal === 'unit') {
        line.textContent = formatMoney(pr.unitTotal); // per-cushion price (spec step)
      } else {
        line.textContent =
          piece.quantity > 1
            ? `${formatMoney(pr.unitTotal)} each · ${formatMoney(pr.lineTotal)}`
            : formatMoney(pr.lineTotal);
      }
    }
    const summary = card.querySelector('[data-summary]');
    if (summary) summary.textContent = this.#pieceSummary(piece, pr);

    // Tint the live preview with the selected fabric's swatch colour.
    card.style.setProperty('--cf-cushion-fill', optionById(CONFIG.fabrics, piece.fabricId).swatch);

    // Contextual guidance: show the selected option's help under each group.
    for (const helpEl of card.querySelectorAll('[data-help-for]')) {
      helpEl.textContent = this.#selectedOption(helpEl.dataset.helpFor, piece)?.help ?? '';
    }

    // Exposure advisories — the recommendation reason, or a location-framed
    // nudge when a lighter option than the stated exposure was picked. Foam and
    // fabric MUST behave identically here (one loop, not two hand-copies);
    // fabric stays advisory-only by design (exposure never re-selects it).
    const exposureNotes = [['[data-foam-note]', foamExposureMessage]];
    // Fabric's exposure note rides with the tier ladder — suppressed at launch
    // (single fabric); the note element just stays empty until guidance is on.
    if (CONFIG.fabricExposureGuidance) exposureNotes.push(['[data-fabric-note]', fabricExposureMessage]);
    for (const [selector, messageFor] of exposureNotes) {
      const note = card.querySelector(selector);
      if (!note) continue;
      const msg = messageFor(piece, this.#order.exposure);
      note.textContent = msg?.text ?? '';
      note.className = `cf__foam-note${msg ? ` cf__foam-note--${msg.kind}` : ''}`;
      note.hidden = !msg;
    }

    // Validation: fill each error slot and flag invalid inputs.
    const { issues } = validatePiece(piece, this.#unit);
    for (const slot of card.querySelectorAll('[data-error]')) {
      const key = slot.dataset.error;
      const msg = issues[key] ?? '';
      slot.textContent = msg;
      for (const input of card.querySelectorAll(`[data-field="${key}"]`)) {
        input.classList.toggle('cf__input--invalid', Boolean(msg));
        input.setAttribute('aria-invalid', String(Boolean(msg)));
      }
    }
    card.classList.toggle('cf__piece--invalid', Object.keys(issues).length > 0);
  }

  #refreshTotal() {
    const priced = priceOrder(this.#order);
    const el = this.querySelector('[data-order-total]');
    if (el) el.textContent = formatMoney(priced.total);
    const sub = this.querySelector('[data-total-breakdown]');
    if (sub) {
      const text = this.#totalBreakdown();
      sub.textContent = text;
      sub.hidden = !text;
    }
    // Multi-buy discount line in the set total (shown once it applies).
    const mbApplies = priced.multiBuyDiscount > 0;
    const mbRate = Math.round((CONFIG.multiBuyDiscount?.rate ?? 0) * 100);
    for (const line of this.querySelectorAll('[data-multibuy-line]')) line.hidden = !mbApplies;
    for (const lbl of this.querySelectorAll('[data-multibuy-label]')) lbl.textContent = `Set discount (${mbRate}%)`;
    for (const amt of this.querySelectorAll('[data-multibuy-amount]')) amt.textContent = `−${formatMoney(priced.multiBuyDiscount)}`;
    this.#refreshGuarantee(priced);
    this.#refreshMultiBuy();
    this.#scheduleDraftSave(); // chokepoint: every model change schedules a draft save
    this.#scheduleAbandonBeacon(); // …and a (much lazier) anonymous telemetry beacon
  }

  /** Live-update the multi-buy nudge as pieces/quantities change. */
  #refreshMultiBuy() {
    const { html, hidden, applied } = this.#multiBuyNudgeParts();
    for (const el of this.querySelectorAll('[data-multibuy]')) {
      el.innerHTML = html;
      el.hidden = hidden;
      el.classList.toggle('cf__multibuy--applied', applied);
    }
  }

  /** Sync the fit-guarantee toggle, its price and the summary line to the order. */
  #refreshGuarantee(priced = priceOrder(this.#order)) {
    const cfg = CONFIG.fitGuarantee;
    if (!cfg || cfg.enabled === false) return;
    const on = this.#order.fitGuarantee === true;
    const amount = fitGuaranteeAmount(priced.subtotal);
    for (const el of this.querySelectorAll('[data-guarantee-amount]')) {
      el.textContent = `+${formatMoney(amount)}`;
    }
    const toggle = this.querySelector('[data-guarantee] .cf__guarantee-toggle');
    if (toggle) toggle.setAttribute('aria-pressed', String(on));
    for (const line of this.querySelectorAll('[data-guarantee-line]')) {
      line.hidden = !on;
    }
  }

  #refreshValidity() {
    const { valid, issueCount } = validateOrder(this.#order);
    const checkout = this.querySelector('[data-action="add-to-cart"], [data-action="submit-quote"]');
    // The made-to-measure tick gates add-to-cart only — a quote request isn't a sale.
    const needsConsent = Boolean(checkout?.matches('[data-action="add-to-cart"]')) && !this.#consentOk();
    const el = this.querySelector('[data-validity]');
    if (el) {
      // Say WHY the button is disabled — a silent dead button reads as broken.
      const message = !valid
        ? `${issueCount} ${issueCount === 1 ? 'field needs' : 'fields need'} attention`
        : needsConsent
          ? 'Please confirm the made-to-measure note above'
          : '';
      el.hidden = !message;
      el.textContent = message;
    }
    if (checkout) checkout.disabled = !valid || needsConsent;
  }

  /* ---- cart ------------------------------------------------------------ */

  async #handleAddToCart() {
    const btn = this.querySelector('[data-action="add-to-cart"]');
    if (!validateOrder(this.#order).valid) {
      this.#refreshValidity();
      return;
    }
    // Belt-and-braces: the button is already disabled without the tick, but never
    // let a build reach the cart unacknowledged (CCR 2013 reg 28(1)(b)).
    if (!this.#consentOk()) {
      this.#refreshValidity();
      this.#setCartStatus('Please confirm you understand these are made to your measurements.');
      return;
    }

    const label = btn.textContent;
    const reset = () => {
      btn.disabled = false;
      btn.textContent = label;
      btn.classList.remove('cf__checkout--success');
    };

    btn.disabled = true;
    btn.textContent = 'Adding…';
    this.#setCartStatus('');

    let result;
    try {
      result = await this.#cart.addOrder(this.#order);
    } catch (error) {
      console.error('[cushion-configurator] add-to-cart failed', error);
      this.#setCartStatus('Sorry — something went wrong adding to your cart. Please try again.');
      reset();
      return;
    }

    if (result.ok && result.result?.mock) {
      // Nothing actually reached a cart — keep the draft (a misconfigured ladder
      // product must not destroy the customer's build).
      this.#setCartStatus('Mock cart: payload logged to the console (the price-ladder product isn’t set up yet).');
      reset();
    } else if (result.ok) {
      this.#clearDraft(); // committed to cart — no longer an unfinished draft
      track('cf_add_to_cart', this.#buildAnalytics());
      this.#markStage('cart');
      this.#abandonFlags.converted = 1;
      this.#scheduleAbandonBeacon({ immediate: true });
      // Kick the sense-check photos off now so they upload while the drawer opens.
      // Resolves instantly if there are none. (Photos are too large for a
      // keepalive fetch, so if we navigate we let this finish first — below.)
      const sensecheck = this.#sendSensecheck();
      // Success — confirm. Prefer opening Horizon's cart drawer (stay in flow);
      // fall back to the cart page if there's no drawer on this storefront.
      btn.textContent = 'Added to cart ✓';
      btn.classList.add('cf__checkout--success');
      if (this.#openCartDrawer(result.result)) {
        this.#setCartStatus('Added to your cart.'); // stays on page — upload finishes in the background
        setTimeout(reset, 2200);
      } else {
        this.#setCartStatus('Your set has been added — taking you to your cart…');
        // Navigating cancels an in-flight upload — give it up to 2.5s to land first.
        Promise.race([sensecheck, new Promise((r) => setTimeout(r, 2500))]).finally(() => {
          window.location.href = '/cart';
        });
      }
    } else if (result.reason === 'over-ladder') {
      // A piece priced beyond the online ladder — a valid but outsized build we
      // can't charge correctly online. Keep the build; route to a human.
      track('cf_over_ladder', this.#buildAnalytics());
      this.#abandonFlags.overLadder = 1;
      this.#scheduleAbandonBeacon({ immediate: true });
      this.#setCartStatus(
        'This set is beyond our online price range — use “Copy share link” and send it to us for a direct quote.'
      );
      reset();
    } else if (result.reason === 'no-variant') {
      this.#setCartStatus('Couldn’t find a matching price tier — the price ladder may need extending.');
      reset();
    } else {
      this.#refreshValidity();
      reset();
    }
  }

  #setCartStatus(message) {
    const el = this.querySelector('[data-cart-status]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /* ---- bespoke quote flow --------------------------------------------- */

  /** Does this order need a hand-priced quote? True if any piece is a shape that
   *  needs a template/photo (bespoke) — those can't be auto-priced on the ladder,
   *  so the whole set is quoted together rather than added to cart. */
  #needsQuote() {
    return this.#order?.pieces.some((p) => pieceQuoteReason(p) != null) ?? false;
  }

  /** The dominant reason the order can't be auto-priced, for reason-aware copy.
   *  Priority: a template need > over-length > out-of-range depth > back-order colour. */
  #quoteReason() {
    const reasons = (this.#order?.pieces ?? []).map((p) => pieceQuoteReason(p)).filter(Boolean);
    for (const r of ['template', 'over-length', 'depth-range', 'fabric-backorder']) {
      if (reasons.includes(r)) return r;
    }
    return null;
  }

  /** The terminal button — add-to-cart for a normal set, or send-quote for bespoke. */
  #checkoutControl() {
    return this.#needsQuote()
      ? `<button type="button" class="cf__checkout" data-action="submit-quote">Send quote request</button>`
      : `<button type="button" class="cf__checkout" data-action="add-to-cart">Add set to cart</button>`;
  }

  /** Stands in for the price total when a build is quote-only (no ladder price). */
  #quotePriceNote() {
    return `<div class="cf__totals"><div class="cf__total"><span class="cf__total-label">Total</span><span class="cf__total-value">Priced by quote</span></div></div>`;
  }

  /** The contact form shown on review/summary for a bespoke build. Contact fields
   *  are read from the DOM on submit — never put on the (pure) capture model. */
  #quoteForm() {
    // Reason-aware copy: a bespoke shape, an over-2m piece (usually made in
    // sections), or a size outside our standard range all route here but read
    // differently to the customer.
    const reason = this.#quoteReason();
    const intro = {
      'over-length':
        'Cushions longer than 2 metres are usually made in two sections, so we price them by hand. Leave your details and we’ll email your quote — usually within one working day. Your measurements come along automatically.',
      'depth-range':
        'This size is outside our standard range, so we price it by hand. Leave your details and we’ll email your quote — usually within one working day. Your measurements come along automatically.',
      'fabric-backorder':
        'One of your colours is a special order, so we bring it in for you and price it by hand — we’ll confirm the cost and lead time before you pay anything. Leave your details and we’ll email you back, usually within one working day. Your measurements and colour choice come along automatically.',
    }[reason] ||
      'Bespoke shapes are made to order, so we price them by hand. Leave your details and we’ll email your quote — usually within one working day. Your measurements come along automatically; a photo helps but isn’t required.';
    const title = reason === 'template' ? 'Request a price for your bespoke design' : 'Request a price for your design';
    return `
      <div class="cf__quote" data-quote-form>
        <h3 class="cf__quote-title">${title}</h3>
        <p class="cf__quote-intro">${intro}</p>
        <div class="cf__quote-grid">
          <label class="cf__quote-label">Your name
            <input class="cf__quote-input" type="text" data-quote-field="name" autocomplete="name" required />
          </label>
          <label class="cf__quote-label">Email
            <input class="cf__quote-input" type="email" data-quote-field="email" autocomplete="email" required />
          </label>
          <label class="cf__quote-label">Phone (optional)
            <input class="cf__quote-input" type="tel" data-quote-field="phone" autocomplete="tel" />
          </label>
          <label class="cf__quote-label cf__quote-label--full">Anything else we should know? (optional)
            <textarea class="cf__quote-input" data-quote-field="message" rows="3"></textarea>
          </label>
        </div>
        <input type="text" class="cf__quote-hp" data-quote-field="website" tabindex="-1" autocomplete="off" aria-hidden="true" />
      </div>`;
  }

  /** Plain-text build summary for the quote email (no price — bespoke is by quote). */
  #quoteSummary() {
    return this.#order.pieces
      .map((p) => {
        const shape = shapeById(p.shape);
        const dims = [...shape.fields.map((f) => p.dimensions[f.key]), p.dimensions[DEPTH_FIELD.key]]
          .map((mm) => formatDim(mm, this.#unit))
          .join(' × ');
        const fabric = optionById(CONFIG.fabrics, p.fabricId).label;
        const foam = p.coverOnly ? 'cover only (no foam)' : optionById(CONFIG.foams, p.foamId).label;
        const piping = optionById(CONFIG.piping, p.pipingId).label;
        const extras = (p.addonIds ?? []).map((id) => findOption(CONFIG.addons, id)?.label).filter(Boolean).join(', ');
        const n = (this.#templates.get(p.id) ?? []).length;
        const photo = n ? ` [${n} photo${n > 1 ? 's' : ''} attached]` : ' [no photo attached]';
        const zipReq = p.zipNote ? `; zip request: ${p.zipNote}` : '';
        return `• ${this.#pieceTitle(p)} — ${shape.label}; ${dims}; ${fabric}; ${foam}; piping: ${piping}${extras ? `; extras: ${extras}` : ''}${zipReq}${photo}`;
      })
      .join('\n');
  }

  /** Downscale + base64-encode the uploaded template photos (kept small for the
   *  email payload). One per bespoke piece, from the live File objects. */
  async #collectQuotePhotos() {
    const photos = [];
    for (const piece of this.#order.pieces) {
      for (const t of this.#templates.get(piece.id) ?? []) {
        const photo = await this.#downscaleImage(t.file).catch(() => null);
        if (photo) photos.push(photo);
      }
    }
    return photos.slice(0, MAX_QUOTE_PHOTOS); // belt-and-braces: never exceed the email/webhook cap
  }

  /** Downscale + encode the order-level sense-check photos for the /sensecheck email. */
  async #collectSensecheckPhotos() {
    const out = [];
    for (const s of this.#sensecheckPhotos) {
      const photo = await this.#downscaleImage(s.file).catch(() => null);
      if (photo) out.push(photo);
    }
    return out.slice(0, CONFIG.sensecheck?.maxPhotos ?? MAX_QUOTE_PHOTOS);
  }

  /** Fire the sense-check photos to the team on a successful add-to-cart.
   *  Fire-and-forget: this must NEVER block or fail the cart — a dropped send
   *  costs a sense check, not the sale. Loosely coupled to the order (the same
   *  build token rides the order's _spec), so the team reconciles by spec. */
  async #sendSensecheck() {
    if (!CONFIG.sensecheck?.enabled || !this.#sensecheckPhotos.length) return;
    let photos;
    try {
      photos = await this.#collectSensecheckPhotos();
    } catch {
      return;
    }
    if (!photos.length) return;
    const payload = {
      token: orderToToken(this.#order),
      summary: this.#quoteSummary(),
      name: this.#customerName || '',
      email: this.#customerEmail || '',
      website: '', // honeypot — no field to fill; app-proxy signature is the real guard
      photos,
    };
    if (!this.#sensecheckEndpoint) {
      console.info('[cushion-configurator] (mock) sense-check photos:', payload);
      return;
    }
    try {
      await fetch(this.#sensecheckEndpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('[cushion-configurator] sense-check send failed', error);
    }
  }

  /** Resize an image File to <= maxDim and re-encode as JPEG → { filename, type, dataBase64 }. */
  #downscaleImage(file, maxDim = 1600, quality = 0.82) {
    return new Promise((resolve) => {
      if (!file.type?.startsWith('image/')) return resolve(null);
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height, 1));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d')?.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        try {
          const dataBase64 = canvas.toDataURL('image/jpeg', quality).split(',')[1] ?? '';
          resolve(dataBase64 ? { filename: `${(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`, type: 'image/jpeg', dataBase64 } : null);
        } catch {
          resolve(null); // tainted canvas / encode failure
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  /** Submit the bespoke quote request to the app-proxy /quote route. */
  async #submitQuote(btn) {
    if (!validateOrder(this.#order).valid) {
      this.#refreshValidity();
      this.#setCartStatus('Please finish your measurements before requesting a quote.');
      return;
    }
    const form = this.querySelector('[data-quote-form]');
    const get = (f) => form?.querySelector(`[data-quote-field="${f}"]`)?.value.trim() ?? '';
    const name = get('name');
    const email = get('email');
    if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#setCartStatus('Please add your name and a valid email so we can send your quote.');
      return;
    }

    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    this.#setCartStatus('');

    const payload = {
      token: orderToToken(this.#order),
      summary: this.#quoteSummary(),
      name,
      email,
      phone: get('phone'),
      message: get('message'),
      website: get('website'), // honeypot
      photos: await this.#collectQuotePhotos(),
    };

    if (!this.#quoteEndpoint) {
      console.info('[cushion-configurator] (mock) quote request:', payload);
      this.#setCartStatus('Quote requests aren’t wired up yet (dev): payload logged to the console.');
      btn.disabled = false;
      btn.textContent = label;
      return;
    }

    try {
      const res = await fetch(this.#quoteEndpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(String(res.status));
      this.#clearDraft();
      track('cf_quote_requested', this.#buildAnalytics());
      this.#abandonFlags.quoted = 1;
      this.#scheduleAbandonBeacon({ immediate: true });
      this.#showQuoteSuccess();
    } catch (error) {
      console.error('[cushion-configurator] quote request failed', error);
      this.#setCartStatus('Sorry — we couldn’t send your request just now. Please try again in a moment.');
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  #showQuoteSuccess() {
    const form = this.querySelector('[data-quote-form]');
    if (form) {
      form.innerHTML = `<div class="cf__quote-done" role="status">
        <strong>Thanks — your request is on its way.</strong>
        <p>We’ll email your quote, usually within one working day. Worth a quick look in your spam folder too, just in case.</p>
      </div>`;
    }
    const btn = this.querySelector('[data-action="submit-quote"]');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Request sent ✓';
      btn.classList.add('cf__checkout--success');
    }
  }

  /** Plain-text build summary for the customer's own design email. Prices are
   *  included as a courtesy but the LINK is the payload — rehydrate re-prices
   *  at current rates, so the email never becomes a stale quote to honour. */
  #designEmailSummary() {
    const quoteOnly = this.#needsQuote();
    return this.#order.pieces
      .map((p) => {
        const shape = shapeById(p.shape);
        const dims = [...shape.fields.map((f) => p.dimensions[f.key]), p.dimensions[DEPTH_FIELD.key]]
          .map((mm) => formatDim(mm, this.#unit))
          .join(' × ');
        const fabric = optionById(CONFIG.fabrics, p.fabricId).label;
        const foam = p.coverOnly ? 'cover only (no foam)' : optionById(CONFIG.foams, p.foamId).label;
        const price = quoteOnly ? '' : ` — ${formatMoney(pricePiece(p).unitTotal)} each`;
        return `• ${this.#pieceTitle(p)} — ${shape.label}; ${dims}; ${fabric}; ${foam}${price}`;
      })
      .join('\n');
  }

  /** Send the current design to the customer's own inbox via the app-proxy
   *  /design-email route. Deliberately does NOT clear the draft — unlike a
   *  quote request, the customer is keeping this build, not handing it over. */
  async #submitDesignEmail(btn) {
    const panel = this.querySelector('[data-email-design]');
    const status = panel?.querySelector('[data-email-design-status]');
    const say = (msg) => {
      if (status) {
        status.textContent = msg;
        status.hidden = !msg;
      }
    };
    const email = panel?.querySelector('[data-email-design-input]')?.value.trim() ?? '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return say('Please enter a valid email address.');

    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    say('');

    const payload = {
      email,
      token: orderToToken(this.#order),
      summary: this.#designEmailSummary(),
      totalText: this.#needsQuote() ? '' : formatMoney(priceOrder(this.#order).total),
      website: panel?.querySelector('[data-email-design-hp]')?.value ?? '', // honeypot
    };

    if (!this.#designEmailEndpoint) {
      console.info('[cushion-configurator] (mock) design email:', payload);
      say('Design emails aren’t wired up yet (dev): payload logged to the console.');
      btn.disabled = false;
      btn.textContent = label;
      return;
    }

    try {
      const res = await fetch(this.#designEmailEndpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(String(res.status));
      track('cf_design_emailed', this.#buildAnalytics());
      if (panel) {
        panel.innerHTML = `<div class="cf__quote-done" role="status">
          <strong>On its way to ${escapeHtml(email)}.</strong>
          <p>The link reopens this exact design whenever you’re ready — worth a quick look in spam if it doesn’t appear in a few minutes.</p>
        </div>`;
      }
    } catch (error) {
      console.error('[cushion-configurator] design email failed', error);
      say('Sorry — we couldn’t send that just now. Please try again in a moment.');
      btn.disabled = false;
      btn.textContent = label;
    }
  }

  /**
   * Open Horizon's cart drawer and refresh its contents. Dispatching CartAddEvent
   * makes the cart-items component re-fetch its section and the cart icon update;
   * we also call open() so the drawer shows regardless of its auto-open setting.
   * @returns {boolean} true if a drawer was found (else the caller redirects).
   */
  #openCartDrawer(cart) {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return false;
    document.dispatchEvent(new CartAddEvent(cart ?? {}, 'cushion-configurator', { source: 'cushion-configurator' }));
    if (typeof drawer.open === 'function') drawer.open();
    return true;
  }

  #refreshAll() {
    for (const piece of this.#order.pieces) {
      this.#refreshPiece(this.querySelector(`[data-piece-id="${piece.id}"]`), piece);
    }
    this.#refreshTotal();
    this.#refreshValidity();
  }

  /** One-line summary shown in the header when the card is collapsed. */
  #pieceSummary(piece, pr = pricePiece(piece)) {
    const shape = shapeById(piece.shape);
    const unit = this.#unit;
    const dims = [...shape.fields.map((fd) => piece.dimensions[fd.key]), piece.dimensions[DEPTH_FIELD.key]]
      .map((mm) => displayValue(mm, unit))
      .join(' × ');
    const fabric = optionById(CONFIG.fabrics, piece.fabricId).label;
    const price = shape.requiresTemplate
      ? 'priced by quote'
      : piece.quantity > 1
        ? `${formatMoney(pr.unitTotal)} × ${piece.quantity} · ${formatMoney(pr.lineTotal)}`
        : formatMoney(pr.lineTotal);
    const cover = piece.coverOnly ? ' · cover only' : '';
    return `${shape.label} · ${dims}${UNITS[unit].suffix} · ${fabric}${cover} · ${price}`;
  }

  /* ---- helpers --------------------------------------------------------- */

  #pieceFromNode(node) {
    const id = node.closest('[data-piece-id]')?.dataset.pieceId;
    return this.#order.pieces.find((p) => p.id === id) ?? null;
  }

  /** The currently-selected option object for a choice field (for guidance text). */
  #selectedOption(field, piece) {
    const list = field === 'shape' ? SHAPES : optionListFor(field);
    return list ? optionById(list, piece[field]) : null;
  }

  #disposeTemplate(id) {
    for (const t of this.#templates.get(id) ?? []) URL.revokeObjectURL(t.url);
    this.#templates.delete(id);
  }

  #disposePiece(id) {
    this.#collapsed.delete(id);
    this.#disposeTemplate(id);
  }
}

if (!customElements.get('cushion-configurator')) {
  customElements.define('cushion-configurator', CushionConfigurator);
}
